/**
 * CrmManager — Method-style CRM with Customers, Leads, Pipeline, Activities, Send Email.
 *
 * Auto-scopes by role:
 *   - admin/super_admin: sees all accounts + all reps
 *   - sales_rep: sees ONLY accounts where owner_id = their rep_id
 *
 * Features:
 *   - Tabs: Customers · Leads · Pipeline (Kanban) · Activities · Send Email
 *   - Add Lead with duplicate-detection popup ("already assigned to {rep}")
 *   - Assign-to-rep dropdown + bulk assign
 *   - Convert Lead → Customer (manual) — auto-fired when first PO invoiced (planned)
 *   - Account detail modal: Overview · Contacts · Activities · Orders
 *   - Activity Modal: Email / Phone / Note / Appointment with mailto + .ics deep links
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Search, Filter, X, Mail, Phone, FileText, Calendar,
  UserPlus, Users, Building2, ArrowRight, CheckCircle, AlertCircle,
  Trash2, Pencil, ExternalLink, Activity, ClipboardList, Send,
  ChevronRight, Briefcase, Heart, FileUp, Hospital, Loader2,
  Clock, MessageSquare, Save, PlayCircle, PauseCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/App";
import AddSalesRepDialog from "./AddSalesRepDialog";
import HospitalQuickEditDialog from "./HospitalQuickEditDialog";
import CampaignQuickSendDialog from "./CampaignQuickSendDialog";
import HospitalProfileDialog from "./HospitalProfileDialog";

const API = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { id: "new",             label: "New",             color: "bg-slate-100 text-slate-700 border-slate-300" },
  { id: "contacted",       label: "Contacted",       color: "bg-blue-100 text-blue-700 border-blue-300" },
  { id: "demo_scheduled",  label: "Demo Scheduled",  color: "bg-indigo-100 text-indigo-700 border-indigo-300" },
  { id: "proposal",        label: "Proposal",        color: "bg-purple-100 text-purple-700 border-purple-300" },
  { id: "won",             label: "Won",             color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  { id: "lost",            label: "Lost",            color: "bg-rose-100 text-rose-700 border-rose-300" },
];
const stageMeta = (id) => STAGES.find((s) => s.id === id) || STAGES[0];

const ACT_META = {
  email:       { icon: Mail,       label: "Email",       color: "text-blue-600" },
  phone:       { icon: Phone,      label: "Phone Call",  color: "text-emerald-600" },
  note:        { icon: FileText,   label: "Note",        color: "text-slate-600" },
  appointment: { icon: Calendar,   label: "Appointment", color: "text-purple-600" },
  other:       { icon: Activity,   label: "Other",       color: "text-amber-600" },
};

const fmt$ = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "—");
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString() : "—");

// "3d ago" / "2h ago" / "just now" — pretty-prints a campaign send timestamp
const relativeTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
};

const CrmManager = ({ scope = "admin" }) => {
  const { token, user } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isAdmin = ["admin", "super_admin", "hub_admin"].includes(user?.role);
  const [tab, setTab] = useState("customers");
  const [accounts, setAccounts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [reps, setReps] = useState([]);
  const [pipeline, setPipeline] = useState({ stages: {}, totals: {} });
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [approvalFilter, setApprovalFilter] = useState("all"); // all | requested | none
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [detailAccount, setDetailAccount] = useState(null);
  const [activityForAccount, setActivityForAccount] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [adminDash, setAdminDash] = useState(null);
  const [repDash, setRepDash] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [requestModalAccount, setRequestModalAccount] = useState(null);
  const [pingingStale, setPingingStale] = useState(false);
  const [domainStats, setDomainStats] = useState([]);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [showInlineAddRep, setShowInlineAddRep] = useState(false);
  const [inlineAddRepRow, setInlineAddRepRow] = useState(null);  // {accountId} – assign rep to this row after create
  const [hospitalForAccount, setHospitalForAccount] = useState(null);
  const [editingAccount, setEditingAccount] = useState(null);  // open AccountModal in edit mode
  const [campaignForAccount, setCampaignForAccount] = useState(null);  // open CRM-embedded campaign dialog

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accs, acts, pipe] = await Promise.all([
        axios.get(`${API}/api/crm/accounts`, { headers }),
        axios.get(`${API}/api/crm/activities`, { headers }),
        axios.get(`${API}/api/crm/pipeline`, { headers }),
      ]);
      setAccounts(accs.data.accounts || []);
      setActivities(acts.data.activities || []);
      setPipeline(pipe.data || { stages: {}, totals: {} });
      if (isAdmin) {
        const [r, dash, pend, dom] = await Promise.all([
          axios.get(`${API}/api/admin/sales-reps`, { headers }),
          axios.get(`${API}/api/crm/dashboard/admin`, { headers }),
          axios.get(`${API}/api/crm/approvals/pending`, { headers }),
          axios.get(`${API}/api/crm/domain-stats`, { headers }),
        ]);
        setReps(r.data.reps || []);
        setAdminDash(dash.data);
        setPendingApprovals(pend.data.approvals || []);
        setDomainStats(dom.data.domains || []);
      } else {
        const dash = await axios.get(`${API}/api/crm/dashboard/me`, { headers });
        setRepDash(dash.data);
      }
    } catch (e) {
      toast.error("Failed to load CRM data");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [headers, isAdmin]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const filtered = useMemo(() => {
    return accounts.filter((a) => {
      if (tab === "customers" && a.status !== "customer") return false;
      if (tab === "leads" && a.status !== "lead") return false;
      if (typeFilter !== "all" && a.type !== typeFilter) return false;
      if (ownerFilter !== "all") {
        if (ownerFilter === "unassigned" && a.owner_id) return false;
        if (ownerFilter !== "unassigned" && a.owner_id !== ownerFilter) return false;
      }
      if (search) {
        const hay = `${a.name || ""} ${a.email || ""} ${a.phone || ""} ${(a.contacts || []).map(c => `${c.name} ${c.email}`).join(" ")}`.toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
      }
      if (approvalFilter === "requested" && a.approval_status !== "requested") return false;
      if (approvalFilter === "none" && a.approval_status === "requested") return false;
      return true;
    });
  }, [accounts, tab, typeFilter, ownerFilter, approvalFilter, search]);

  const repName = (id) => reps.find((r) => r.id === id)?.name || "Unassigned";

  const onAssign = async (acc, repId) => {
    try {
      await axios.post(`${API}/api/crm/accounts/${acc.id}/assign`, { rep_id: repId }, { headers });
      toast.success(`Assigned ${acc.name} to ${repName(repId)}`);
      load();
    } catch (e) { toast.error("Assign failed"); }
  };

  const bulkAssignUnassigned = async () => {
    if (!isAdmin) return;
    const repId = window.prompt(
      `Bulk-assign ALL unassigned accounts. Paste the rep id, or pick a rep below.\n\nReps:\n${reps.map((r) => `  ${r.id}  →  ${r.name}`).join("\n")}\n\nEnter rep id:`,
      reps[0]?.id || ""
    );
    if (!repId) return;
    const rep = reps.find((r) => r.id === repId);
    if (!rep) { toast.error("Rep id not found"); return; }
    if (!window.confirm(`Assign every unassigned account to ${rep.name}?`)) return;
    try {
      const r = await axios.post(`${API}/api/crm/accounts/bulk-assign`, { rep_id: repId, scope: "unassigned" }, { headers });
      toast.success(`Assigned ${r.data.modified} accounts to ${rep.name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk assign failed"); }
  };

  const onConvert = async (acc) => {
    // Sales reps must REQUEST approval; admins can convert directly.
    if (!isAdmin) {
      setRequestModalAccount(acc);
      return;
    }
    if (!window.confirm(`Convert "${acc.name}" from Lead to Customer? (Auto-creates Hospital Portal entry if applicable.)`)) return;
    try {
      await axios.post(`${API}/api/crm/accounts/${acc.id}/convert`, {}, { headers });
      toast.success(`${acc.name} converted to Customer`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Convert failed"); }
  };

  const submitConversionRequest = async ({ body, estimated_first_po_value, expected_close_date }) => {
    if (!requestModalAccount) return;
    try {
      const r = await axios.post(
        `${API}/api/crm/accounts/${requestModalAccount.id}/request-conversion`,
        { body, estimated_first_po_value, expected_close_date },
        { headers },
      );
      toast.success(`Submitted ${requestModalAccount.name} for admin approval`);
      // Auto-open Outlook compose so the rep can send the admin a heads-up email
      if (r.data?.admin_mailto) {
        try { window.open(r.data.admin_mailto, "_blank"); } catch { /* popup blocked – non-fatal */ }
      }
      setRequestModalAccount(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Request failed");
    }
  };

  const onPingStale = async () => {
    if (pingingStale) return;
    setPingingStale(true);
    try {
      const r = await axios.post(`${API}/api/crm/approvals/notify-stale`, {}, { headers });
      const { stale_count, pinged_admins } = r.data || {};
      if (!stale_count) {
        toast.info("No stale approvals (>48h) to ping");
      } else {
        toast.success(`Pinged ${pinged_admins} admin(s) about ${stale_count} stale request(s)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ping failed");
    } finally {
      setPingingStale(false);
    }
  };

  const onApprove = async (acc) => {
    if (!window.confirm(`Approve "${acc.name}" → Customer? This auto-creates a Hospital Portal record.`)) return;
    try {
      await axios.post(`${API}/api/crm/accounts/${acc.id}/approve-conversion`, {}, { headers });
      toast.success(`${acc.name} approved & added to Hospital Portal`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };

  const onReject = async (acc) => {
    const reason = window.prompt(`Reject "${acc.name}"?\nReason (sent to rep):`, "Need more discovery before converting.");
    if (reason === null) return;
    try {
      await axios.post(`${API}/api/crm/accounts/${acc.id}/reject-conversion`, { reason }, { headers });
      toast.info(`Rejected — rep notified`);
      load();
    } catch (e) { toast.error("Reject failed"); }
  };

  // Stale-approval reminder via Outlook (mailto:) — pings the rep that admin
  // is reviewing. Used in the admin pending queue when waiting > 48h.
  const remindRep = (p) => {
    const repEmail = p?.requested_by_rep?.email;
    if (!repEmail) { toast.error("Rep has no email on file"); return; }
    const subject = `Re: Customer-Approval Request — ${p.name}`;
    const body = [
      `Hi ${(p.requested_by_rep?.name || "team").split(" ")[0]},`,
      "",
      `Following up on your customer-approval request for ${p.name} from ${fmtDate(p.approval_requested_at)} (${p.hours_waiting}h ago).`,
      "",
      "I'm reviewing now — will get you a decision today.",
      "",
      "George",
    ].join("\n");
    const url = `mailto:${encodeURIComponent(repEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  const syncToHospitalPortal = async () => {
    if (!isAdmin) return;
    try {
      // Dry-run preview first
      const dry = await axios.post(`${API}/api/crm/sync-to-hospital-portal`, { dry_run: true }, { headers });
      const { created, linked_existing, total_processed } = dry.data;
      if (total_processed === 0) {
        toast.info("Hospital Portal is already in sync — nothing to do.");
        return;
      }
      const ok = window.confirm(
        `Sync to Hospital Portal\n\n` +
        `• ${created} new Hospital Portal entries will be created\n` +
        `• ${linked_existing} CRM customers will be linked to existing Hospital Portal records\n\n` +
        `Only customers with type=Hospital are synced. Leads + non-healthcare accounts are skipped. Proceed?`
      );
      if (!ok) return;
      const r = await axios.post(`${API}/api/crm/sync-to-hospital-portal`, {}, { headers });
      toast.success(`Synced — ${r.data.created} created, ${r.data.linked_existing} linked`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
  };

  const onDelete = async (acc) => {
    if (!window.confirm(`Delete "${acc.name}"? This removes all activities too.`)) return;
    try {
      await axios.delete(`${API}/api/crm/accounts/${acc.id}`, { headers });
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-5" data-testid="crm-manager">
      {/* === Pending Approval Queue (admin only) === */}
      {isAdmin && pendingApprovals.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4" data-testid="approval-queue">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-amber-900 inline-flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {pendingApprovals.length} pending Customer-Approval{pendingApprovals.length === 1 ? "" : "s"}
              {pendingApprovals.filter((p) => p.stale).length > 0 && (
                <span className="text-xs font-bold bg-rose-600 text-white px-2 py-0.5 rounded animate-pulse" data-testid="stale-count-badge">
                  {pendingApprovals.filter((p) => p.stale).length} STALE &gt; 48h
                </span>
              )}
            </h3>
            <div className="flex items-center gap-2">
              {pendingApprovals.some((p) => p.stale) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onPingStale}
                  disabled={pingingStale}
                  className="border-rose-300 text-rose-700 hover:bg-rose-50 gap-1"
                  data-testid="approval-ping-stale-btn"
                >
                  <AlertCircle className="w-3 h-3" />
                  {pingingStale ? "Pinging…" : "Ping all stale via Telegram"}
                </Button>
              )}
              <span className="text-xs text-amber-700">Approvals auto-create a Hospital Portal entry on accept.</span>
            </div>
          </div>
          <div className="space-y-2">
            {pendingApprovals.map((p) => {
              const acc = accounts.find((x) => x.id === p.id);
              return (
                <div key={p.id} className={`bg-white rounded border p-3 flex justify-between items-center ${p.stale ? "border-rose-400 ring-1 ring-rose-300" : "border-amber-200"}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => acc && setDetailAccount(acc)} className="font-bold text-blue-700 hover:underline" data-testid={`approval-open-${p.id}`}>{p.name}</button>
                      {p.stale && (
                        <span className="text-[10px] uppercase font-bold bg-rose-600 text-white px-1.5 py-0.5 rounded animate-pulse">
                          Stale · {p.hours_waiting}h
                        </span>
                      )}
                      {!p.stale && p.hours_waiting != null && (
                        <span className="text-[10px] text-slate-500">{p.hours_waiting}h waiting</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      {p.email || "—"} · requested by <strong>{p.requested_by_rep?.name || "—"}</strong> on {fmtDate(p.approval_requested_at)}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {p.stale && p.requested_by_rep?.email && (
                      <Button size="sm" variant="outline" onClick={() => remindRep(p)} className="border-rose-300 text-rose-700 hover:bg-rose-50 gap-1" data-testid={`approval-remind-${p.id}`}>
                        <Mail className="w-4 h-4" /> Reply via Outlook
                      </Button>
                    )}
                    <Button size="sm" onClick={() => acc && onApprove(acc)} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid={`approval-accept-${p.id}`}>
                      <CheckCircle className="w-4 h-4" /> Approve & Add to Portal
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => acc && onReject(acc)} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`approval-reject-${p.id}`}>
                      Reject
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* === Top stats — admin or rep === */}
      {isAdmin && adminDash && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat color="slate"   label="All Accounts" v={adminDash.totals.accounts} />
          <Stat color="amber"   label="Leads"        v={adminDash.totals.leads} />
          <Stat color="emerald" label="Customers"    v={adminDash.totals.customers} />
          <Stat color="blue"    label="Open Activities" v={adminDash.totals.open_activities} />
          <Stat color="rose"    label="Unassigned"   v={adminDash.totals.unassigned} highlight={adminDash.totals.unassigned > 0} />
        </div>
      )}
      {!isAdmin && repDash && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Stat color="slate"   label="My Accounts" v={repDash.totals.accounts} />
          <Stat color="amber"   label="My Leads"    v={repDash.totals.leads} />
          <Stat color="emerald" label="My Customers" v={repDash.totals.customers} />
          <Stat color="blue"    label="Open To-Dos" v={repDash.totals.open_activities} />
          <Stat color="purple"  label="Pipeline $"  v={fmt$(repDash.totals.pipeline_value)} />
          <button
            onClick={() => setApprovalFilter(approvalFilter === "requested" ? "all" : "requested")}
            className={`text-left rounded-lg border p-4 transition ${
              repDash.totals.pending_approvals > 0
                ? `bg-rose-50 border-rose-300 text-rose-800 hover:ring-2 hover:ring-rose-400 ${approvalFilter === "requested" ? "ring-2 ring-rose-500" : ""}`
                : "bg-slate-50 border-slate-200 text-slate-700"
            }`}
            data-testid="rep-pending-approvals-chip"
          >
            <div className="text-[10px] uppercase tracking-wide font-bold opacity-80 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> Awaiting George
            </div>
            <div className="text-2xl font-bold mt-1">
              {repDash.totals.pending_approvals}
              {repDash.totals.stale_approvals > 0 && (
                <span className="text-xs font-bold text-rose-600 ml-1">· {repDash.totals.stale_approvals} stale</span>
              )}
            </div>
            <div className="text-[10px] opacity-70 mt-0.5">
              {repDash.totals.pending_approvals === 0 ? "All caught up" : approvalFilter === "requested" ? "filter active — click to clear" : "click to filter"}
            </div>
          </button>
        </div>
      )}

      {/* === Tabs === */}
      <div className="border-b border-slate-200 flex gap-1 overflow-x-auto">
        {[
          { id: "customers",  label: "Customers",  icon: Users },
          { id: "leads",      label: "Leads",      icon: UserPlus },
          { id: "pipeline",   label: "Pipeline",   icon: Briefcase },
          { id: "activities", label: "Activities", icon: Activity },
          { id: "send-email", label: "Send Email", icon: Send },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-bold border-b-2 whitespace-nowrap inline-flex items-center gap-2 ${
              tab === id ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`crm-tab-${id}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* === Customers / Leads list === */}
      {(tab === "customers" || tab === "leads") && (
        <>
          <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by name, email, phone, contact..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="crm-search"
              />
            </div>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white" data-testid="crm-type-filter">
              <option value="all">All types</option>
              <option value="hospital">Hospitals</option>
              <option value="non_healthcare">Non-Healthcare</option>
            </select>
            {isAdmin && (
              <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white" data-testid="crm-owner-filter">
                <option value="all">All reps</option>
                <option value="unassigned">⚠ Unassigned</option>
                {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            )}
            <select value={approvalFilter} onChange={(e) => setApprovalFilter(e.target.value)} className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white" data-testid="crm-approval-filter">
              <option value="all">Approval: all</option>
              <option value="requested">⏳ Awaiting approval</option>
              <option value="none">No request</option>
            </select>
            <Button onClick={() => setShowAdd(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="crm-add-btn">
              <Plus className="w-4 h-4" /> New {tab === "leads" ? "Lead" : "Customer"}
            </Button>
            {isAdmin && (
              <Button onClick={syncToHospitalPortal} variant="outline" className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="crm-sync-portal-btn">
                <Building2 className="w-4 h-4" /> Sync → Hospital Portal
              </Button>
            )}
            {isAdmin && tab === "customers" && (
              <Button
                onClick={() => document.getElementById("crm-qb-import-input")?.click()}
                variant="outline"
                className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="crm-qb-import-btn"
              >
                <FileUp className="w-4 h-4" /> Import QuickBooks CSV
              </Button>
            )}
            <input
              id="crm-qb-import-input"
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                const fd = new FormData();
                fd.append("file", file);
                const toastId = toast.loading(`Importing ${file.name}…`);
                try {
                  const res = await axios.post(`${API}/api/crm/accounts/import-quickbooks`, fd, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  const d = res.data || {};
                  toast.success(
                    `Imported ${d.imported} customer${d.imported === 1 ? "" : "s"}` +
                    (d.skipped_duplicates ? ` · skipped ${d.skipped_duplicates} duplicate${d.skipped_duplicates === 1 ? "" : "s"}` : "") +
                    (d.skipped_blank ? ` · skipped ${d.skipped_blank} blank row${d.skipped_blank === 1 ? "" : "s"}` : ""),
                    { id: toastId, duration: 8000 }
                  );
                  load();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Import failed", { id: toastId });
                }
              }}
              data-testid="crm-qb-import-input"
            />
            {isAdmin && (
              <Button onClick={() => setShowDomainModal(true)} variant="outline" className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="crm-domain-btn">
                <Briefcase className="w-4 h-4" /> Reassign by Domain
              </Button>
            )}
            {isAdmin && adminDash?.totals?.unassigned > 0 && (
              <Button onClick={bulkAssignUnassigned} variant="outline" className="gap-2 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="crm-bulk-assign-btn">
                <UserPlus className="w-4 h-4" />
                Bulk-Assign {adminDash.totals.unassigned} Unassigned
              </Button>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="crm-accounts-table">
              <thead className="bg-slate-50 border-b text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Account</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Primary Contact</th>
                  <th className="px-3 py-2 text-left">Stage</th>
                  <th className="px-3 py-2 text-right">Lifetime $</th>
                  <th className="px-3 py-2 text-center">Health</th>
                  <th className="px-3 py-2 text-left">Owner</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Loading...</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-12 text-slate-400">
                    No {tab} match the current filters.
                  </td></tr>
                )}
                {!loading && filtered.map((a) => {
                  const stage = stageMeta(a.pipeline_stage);
                  const primary = (a.contacts || []).find((c) => c.is_primary) || (a.contacts || [])[0];
                  return (
                    <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`crm-row-${a.id}`}>
                      <td className="px-3 py-2">
                        <button onClick={() => setDetailAccount(a)} className="text-blue-600 hover:underline text-left font-semibold" data-testid={`crm-open-${a.id}`}>
                          {a.name}
                        </button>
                        {a.is_legacy && <span className="ml-2 text-[9px] uppercase font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Legacy</span>}
                        {a.approval_status === "requested" && <span className="ml-1 text-[9px] uppercase font-bold bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded animate-pulse" data-testid={`crm-pending-badge-${a.id}`}>⏳ Awaiting</span>}
                        {a.approval_status === "rejected" && <span className="ml-1 text-[9px] uppercase font-bold bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">Rejected</span>}
                        <div className="text-xs text-slate-500">{a.email || a.phone || "—"}</div>
                        {a.last_campaign?.sent_at && (
                          <button
                            onClick={() => setDetailAccount(a)}
                            className="mt-1 inline-flex items-center gap-1 text-[10px] bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5 max-w-[280px] truncate"
                            title={`${a.last_campaign.subject || "(no subject)"} · sent ${fmtDateTime(a.last_campaign.sent_at)}${a.last_campaign.recipient_count ? ` · ${a.last_campaign.recipient_count} recipient${a.last_campaign.recipient_count === 1 ? "" : "s"}` : ""}`}
                            data-testid={`crm-last-campaign-${a.id}`}
                          >
                            <Mail className="w-3 h-3 shrink-0" />
                            <span className="font-semibold">Last campaign:</span>
                            <span className="truncate">
                              {relativeTime(a.last_campaign.sent_at)}
                              {a.last_campaign.subject ? ` · ${a.last_campaign.subject}` : ""}
                            </span>
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {a.type === "hospital" ? (
                          <span className="text-xs inline-flex items-center gap-1 text-rose-700"><Heart className="w-3 h-3" /> Hospital</span>
                        ) : (
                          <span className="text-xs inline-flex items-center gap-1 text-slate-700"><Building2 className="w-3 h-3" /> Non-Healthcare</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700">
                        {primary ? (
                          <>
                            <div className="font-medium">{primary.name || primary.email}</div>
                            <div className="text-slate-500">{primary.email}</div>
                          </>
                        ) : <span className="text-slate-400 italic">No contacts</span>}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded border ${stage.color}`}>{stage.label}</span>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-700">{fmt$(a.lifetime_value)}</td>
                      <td className="px-3 py-2 text-center">
                        <HealthChip score={a.health_score || 10} />
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {isAdmin ? (
                          <select
                            value={a.owner_id || ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "__add_new__") {
                                setInlineAddRepRow({ accountId: a.id });
                                setShowInlineAddRep(true);
                                return;
                              }
                              if (v) onAssign(a, v);
                            }}
                            className="border border-slate-300 rounded px-2 py-1 text-xs bg-white max-w-[140px]"
                            data-testid={`crm-assign-${a.id}`}
                          >
                            <option value="">Unassigned</option>
                            {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                            <option value="__add_new__">＋ Add new rep…</option>
                          </select>
                        ) : (
                          <span className="text-slate-600">{repName(a.owner_id)}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1">
                          {a.type === "hospital" && (
                            <button
                              onClick={() => setHospitalForAccount(a)}
                              className="p-1 text-slate-500 hover:text-rose-600"
                              title="Manage hospital (contacts, domains, billing)"
                              data-testid={`crm-manage-hospital-${a.id}`}
                            >
                              <Hospital className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => setActivityForAccount(a)} className="p-1 text-slate-500 hover:text-emerald-600" title="Log activity" data-testid={`crm-activity-${a.id}`}>
                            <Activity className="w-4 h-4" />
                          </button>
                          {a.status === "lead" && (
                            <button onClick={() => onConvert(a)} className="p-1 text-emerald-600 hover:text-emerald-800" title="Convert to Customer" data-testid={`crm-convert-${a.id}`}>
                              <ArrowRight className="w-4 h-4" />
                            </button>
                          )}
                          {isAdmin && (
                            <button onClick={() => onDelete(a)} className="p-1 text-slate-500 hover:text-red-600" title="Delete" data-testid={`crm-delete-${a.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* === Pipeline Kanban === */}
      {tab === "pipeline" && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {STAGES.map((s) => {
            const items = pipeline.stages[s.id] || [];
            const total = pipeline.totals[s.id] || { count: 0, value: 0 };
            return (
              <div key={s.id} className={`rounded-lg border ${s.color} p-3 min-h-[300px] flex flex-col`} data-testid={`pipeline-col-${s.id}`}>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-bold text-sm uppercase tracking-wide">{s.label}</span>
                  <span className="text-xs">{total.count}</span>
                </div>
                <div className="text-xs opacity-70 mb-3">{fmt$(total.value)}</div>
                <div className="space-y-2 flex-1 overflow-y-auto">
                  {items.length === 0 && <p className="text-xs italic opacity-60">No accounts</p>}
                  {items.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => {
                        const full = accounts.find((x) => x.id === a.id);
                        if (full) setDetailAccount(full);
                      }}
                      className="w-full text-left bg-white rounded p-2 shadow-sm hover:shadow-md transition-shadow text-xs border border-slate-200"
                      data-testid={`pipeline-card-${a.id}`}
                    >
                      <div className="font-bold text-slate-800 truncate">{a.name}</div>
                      <div className="text-slate-500 truncate">{a.email || a.phone || "—"}</div>
                      <div className="flex justify-between items-center mt-1 text-[10px]">
                        <span className="text-emerald-600 font-semibold">{fmt$(a.lifetime_value)}</span>
                        <HealthChip score={a.health_score || 10} small />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* === Activities list === */}
      {tab === "activities" && (
        <ActivitiesList
          activities={activities}
          headers={headers}
          isAdmin={isAdmin}
          reps={reps}
          accounts={accounts}
          onChanged={load}
          onOpenAccount={(accId) => {
            const a = accounts.find((x) => x.id === accId);
            if (a) setDetailAccount(a);
          }}
          onOpenActivity={(a) => setEditingActivity(a)}
          onSendCampaign={() => {
            // Without a specific account, open the dialog with the "pick an account" UI
            setCampaignForAccount({ __pickAccount: true, accounts });
          }}
        />
      )}

      {/* === Send Email tab === */}
      {tab === "send-email" && (
        <SendEmailComposer accounts={accounts} headers={headers} onLogged={load} />
      )}

      {/* === Modals === */}
      {showAdd && (
        <AccountModal
          headers={headers}
          isAdmin={isAdmin}
          currentUser={user}
          reps={reps}
          setReps={setReps}
          defaultStatus={tab === "customers" ? "customer" : "lead"}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}

      {editingAccount && (
        <AccountModal
          headers={headers}
          isAdmin={isAdmin}
          currentUser={user}
          reps={reps}
          setReps={setReps}
          existingAccount={editingAccount}
          onClose={() => setEditingAccount(null)}
          onSaved={() => {
            const refreshedId = editingAccount.id;
            setEditingAccount(null);
            load();
            // If detail drawer is open on this account, refetch it to reflect changes
            if (detailAccount?.id === refreshedId) {
              axios.get(`${API}/api/crm/accounts/${refreshedId}`, { headers })
                .then((r) => setDetailAccount(r.data))
                .catch(() => {});
            }
          }}
        />
      )}

      {campaignForAccount && (
        <CampaignQuickSendDialog
          headers={headers}
          account={campaignForAccount}
          onClose={() => setCampaignForAccount(null)}
          onSent={() => { setCampaignForAccount(null); load(); }}
        />
      )}

      {/* Inline "Add new rep…" dialog — triggered from the per-row OWNER picker. */}
      <AddSalesRepDialog
        open={showInlineAddRep}
        onOpenChange={(o) => {
          setShowInlineAddRep(o);
          if (!o) setInlineAddRepRow(null);
        }}
        token={token}
        currentUserRole={user?.role}
        onCreated={async (rep) => {
          // Refresh reps list so the dropdown picks up the new entry
          await load();
          // If they came in from a row dropdown AND the new rep is assignable, auto-assign
          if (inlineAddRepRow && rep?.id) {
            const acc = accounts.find((a) => a.id === inlineAddRepRow.accountId);
            if (acc) await onAssign(acc, rep.id);
          }
        }}
      />
      {detailAccount && (
        <AccountDetailModal
          headers={headers}
          isAdmin={isAdmin}
          reps={reps}
          accountId={detailAccount.id}
          onClose={() => setDetailAccount(null)}
          onChanged={load}
          onLogActivity={() => { setActivityForAccount(detailAccount); }}
          onEdit={(acc) => setEditingAccount(acc)}
          onSendCampaign={(acc) => setCampaignForAccount(acc)}
        />
      )}
      {activityForAccount && (
        <ActivityModal
          headers={headers}
          account={activityForAccount}
          reps={reps}
          isAdmin={isAdmin}
          onClose={() => setActivityForAccount(null)}
          onSaved={() => { setActivityForAccount(null); load(); }}
        />
      )}
      {editingActivity && (
        <ActivityDetailModal
          headers={headers}
          activity={editingActivity}
          reps={reps}
          accounts={accounts}
          onClose={() => setEditingActivity(null)}
          onSaved={() => { setEditingActivity(null); load(); }}
          onDeleted={() => { setEditingActivity(null); load(); }}
        />
      )}
      <HospitalQuickEditDialog
        open={!!hospitalForAccount}
        onOpenChange={(o) => { if (!o) setHospitalForAccount(null); }}
        account={hospitalForAccount}
        token={token}
        onSaved={() => load()}
      />
      {showDomainModal && (
        <DomainReassignModal
          headers={headers}
          reps={reps}
          domains={domainStats}
          onClose={() => setShowDomainModal(false)}
          onSaved={() => { setShowDomainModal(false); load(); }}
        />
      )}
      {requestModalAccount && (
        <RequestConversionModal
          account={requestModalAccount}
          onClose={() => setRequestModalAccount(null)}
          onSubmit={submitConversionRequest}
        />
      )}
    </div>
  );
};

// =============================================================================
// COMPONENTS
// =============================================================================
const Stat = ({ color, label, v, highlight }) => {
  const cls = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
    purple: "bg-purple-50 border-purple-200 text-purple-800",
  }[color] || "bg-slate-50 border-slate-200 text-slate-700";
  return (
    <div className={`border rounded-lg p-4 ${cls} ${highlight ? "ring-2 ring-rose-400" : ""}`}>
      <div className="text-[10px] uppercase tracking-wide font-bold opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-1">{v}</div>
    </div>
  );
};

const HealthChip = ({ score, small }) => {
  let cls = "bg-slate-100 text-slate-700";
  if (score >= 70) cls = "bg-emerald-100 text-emerald-800";
  else if (score >= 40) cls = "bg-amber-100 text-amber-800";
  else if (score >= 20) cls = "bg-orange-100 text-orange-800";
  else if (score < 10) cls = "bg-rose-100 text-rose-800";
  return <span className={`${small ? "text-[9px]" : "text-xs"} px-1.5 py-0.5 rounded font-bold ${cls}`}>{score}</span>;
};

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
    {children}
  </div>
);

// -----------------------------------------------------------------------------
// Add Account modal — with duplicate-detection popup
// -----------------------------------------------------------------------------
const AccountModal = ({ headers, isAdmin, currentUser, reps, setReps, defaultStatus = "lead", onClose, onSaved, existingAccount = null }) => {
  // existingAccount: if provided, we're in EDIT mode (PATCH); else CREATE mode (POST).
  const isEdit = !!existingAccount;
  const [form, setForm] = useState(
    isEdit
      ? {
          name: existingAccount.name || "",
          type: existingAccount.type || "hospital",
          status: existingAccount.status || defaultStatus,
          pipeline_stage: existingAccount.pipeline_stage || (existingAccount.status === "customer" ? "won" : "new"),
          email: existingAccount.email || "",
          phone: existingAccount.phone || "",
          website: existingAccount.website || "",
          billing_address: existingAccount.billing_address || "",
          shipping_address: existingAccount.shipping_address || "",
          health_score: existingAccount.health_score || 10,
          owner_id: existingAccount.owner_id || "",
          notes: existingAccount.notes || "",
        }
      : {
          name: "", type: "hospital", status: defaultStatus, pipeline_stage: defaultStatus === "customer" ? "won" : "new",
          email: "", phone: "", website: "", billing_address: "", shipping_address: "",
          health_score: 10, owner_id: "", notes: "",
        }
  );
  // Multi-contact rows (Name · Title · Email · Phone · Primary).
  // One row blank by default in CREATE mode; pre-loaded from `contacts` in EDIT mode.
  const [contacts, setContacts] = useState(() => {
    if (isEdit && (existingAccount.contacts || []).length > 0) {
      return existingAccount.contacts.map((c) => ({
        name: c.name || "", title: c.title || "", email: c.email || "", phone: c.phone || "",
        is_primary: !!c.is_primary,
      }));
    }
    return [{ name: "", title: "", email: "", phone: "", is_primary: true }];
  });
  const addContactRow = () => setContacts((arr) => [...arr, { name: "", title: "", email: "", phone: "", is_primary: false }]);
  const removeContactRow = (i) => setContacts((arr) => arr.filter((_, ix) => ix !== i));
  const setContactRow = (i, k, v) =>
    setContacts((arr) => arr.map((c, ix) => (ix === i ? { ...c, [k]: v } : c)));
  const togglePrimary = (i) =>
    setContacts((arr) => arr.map((c, ix) => ({ ...c, is_primary: ix === i })));

  const [saving, setSaving] = useState(false);
  const [dupe, setDupe] = useState(null);

  // === Healthcare Network Affiliation state =================================
  const [isPartOfNetwork, setIsPartOfNetwork] = useState(
    !!(isEdit && (existingAccount.is_part_of_network || existingAccount.parent_health_system_id))
  );
  const [parentNetworkId, setParentNetworkId] = useState(
    isEdit ? (existingAccount.parent_health_system_id || "") : ""
  );
  const [networks, setNetworks] = useState([]);
  const [networksLoading, setNetworksLoading] = useState(false);
  const [showNewNetworkForm, setShowNewNetworkForm] = useState(false);
  const [newNetworkName, setNewNetworkName] = useState("");
  const [newNetworkState, setNewNetworkState] = useState("");
  const [creatingNetwork, setCreatingNetwork] = useState(false);

  // Load existing networks once when the user toggles "part of network" on
  useEffect(() => {
    if (!isPartOfNetwork || networks.length > 0) return;
    let cancelled = false;
    (async () => {
      setNetworksLoading(true);
      try {
        const r = await axios.get(`${API}/api/health-systems/networks`);
        if (!cancelled) setNetworks(r.data?.networks || []);
      } catch {
        if (!cancelled) toast.error("Could not load networks list");
      } finally {
        if (!cancelled) setNetworksLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isPartOfNetwork, networks.length]);

  const createNewNetwork = async () => {
    const cleanName = (newNetworkName || "").trim();
    if (!cleanName) {
      toast.error("Network name is required");
      return;
    }
    setCreatingNetwork(true);
    try {
      const r = await axios.post(
        `${API}/api/health-systems/quick-create-network`,
        { name: cleanName, state: newNetworkState || null },
      );
      const net = r.data?.network;
      if (!net?.id) throw new Error("No network id returned");
      // Add to list and auto-select (idempotent on backend — safe to push)
      setNetworks((prev) => {
        const exists = prev.some((p) => p.id === net.id);
        const merged = exists ? prev : [...prev, { id: net.id, name: net.name }];
        return merged.sort((a, b) => a.name.localeCompare(b.name));
      });
      setParentNetworkId(net.id);
      setShowNewNetworkForm(false);
      setNewNetworkName("");
      setNewNetworkState("");
      toast.success(
        r.data?.created
          ? `${net.name} created and linked as parent network`
          : `${net.name} already existed — linked as parent network`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create network");
    } finally {
      setCreatingNetwork(false);
    }
  };
  // Inline "Add new sales rep" sub-form
  const [showAddRep, setShowAddRep] = useState(false);
  const [newRep, setNewRep] = useState({ name: "", email: "", phone: "", default_commission_pct: 15 });
  const [creatingRep, setCreatingRep] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Create a sales-rep record from the inline form, auto-select it as the owner.
  // If `seed` is provided (e.g. the current admin's identity), use that without
  // showing the sub-form — used by the "Assign to me" shortcut.
  const createSalesRep = async (seed) => {
    const source = seed || newRep;
    const cleanName = (source.name || "").trim();
    const cleanEmail = (source.email || "").trim();
    if (!cleanName || !cleanEmail) {
      toast.error("Rep name and email are required");
      return null;
    }
    setCreatingRep(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/sales-reps`,
        {
          name: cleanName,
          email: cleanEmail,
          phone: source.phone || null,
          default_commission_pct: Number(source.default_commission_pct) || 15,
          role_label: source.role_label || "Sales Rep",
        },
        { headers },
      );
      const created = r.data;
      if (setReps) setReps((prev) => [...(prev || []), created].sort((a, b) => a.name.localeCompare(b.name)));
      set("owner_id", created.id);
      toast.success(`Added ${created.name} · auto-assigned`);
      setShowAddRep(false);
      setNewRep({ name: "", email: "", phone: "", default_commission_pct: 15 });
      return created;
    } catch (e) {
      // If the rep already exists (e.g. admin clicked "Assign to me" twice),
      // surface the existing one rather than erroring out.
      const detail = e.response?.data?.detail || "";
      if (e.response?.status === 400 && detail.toLowerCase().includes("already exists") && cleanEmail) {
        const existing = (reps || []).find(
          (rp) => (rp.email || "").toLowerCase() === cleanEmail.toLowerCase(),
        );
        if (existing) {
          set("owner_id", existing.id);
          toast.success(`${existing.name} is already a rep · auto-assigned`);
          setShowAddRep(false);
          return existing;
        }
      }
      toast.error(detail || "Failed to add rep");
      return null;
    } finally {
      setCreatingRep(false);
    }
  };

  const assignToMe = () => {
    if (!currentUser?.email) {
      toast.error("No admin identity available");
      return;
    }
    // If the admin already has a matching sales-rep entry, just select it.
    const myEmail = (currentUser.email || "").toLowerCase();
    const mine = (reps || []).find((rp) => (rp.email || "").toLowerCase() === myEmail);
    if (mine) {
      set("owner_id", mine.id);
      toast.success(`Assigned to ${mine.name}`);
      return;
    }
    createSalesRep({
      name: currentUser.name || currentUser.email,
      email: currentUser.email,
      phone: currentUser.phone || null,
      default_commission_pct: 15,
      role_label: "Administrator",
    });
  };

  const checkDup = async () => {
    if (!form.name.trim()) return;
    try {
      const r = await axios.get(`${API}/api/crm/accounts/check-duplicate`, {
        headers, params: { name: form.name, email: form.email || undefined },
      });
      setDupe(r.data.exists ? r.data : null);
    } catch (e) { /* non-fatal */ }
  };

  const submit = async (force = false) => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    // Filter out empty contact rows; keep only ones with at least an email or name
    const cleanContacts = contacts
      .map((c) => ({
        name: (c.name || "").trim(),
        title: (c.title || "").trim() || null,
        email: (c.email || "").trim().toLowerCase() || null,
        phone: (c.phone || "").trim() || null,
        is_primary: !!c.is_primary,
      }))
      .filter((c) => c.name || c.email);
    // Force exactly one primary (or zero if no contacts)
    if (cleanContacts.length > 0 && !cleanContacts.some((c) => c.is_primary)) {
      cleanContacts[0].is_primary = true;
    }
    if (cleanContacts.filter((c) => c.is_primary).length > 1) {
      const firstIdx = cleanContacts.findIndex((c) => c.is_primary);
      cleanContacts.forEach((c, ix) => { c.is_primary = ix === firstIdx; });
    }

    setSaving(true);
    try {
      if (isEdit) {
        // PATCH the account fields, then reconcile contacts (add/update/remove)
        await axios.patch(
          `${API}/api/crm/accounts/${existingAccount.id}`,
          {
            ...form,
            is_part_of_network: isPartOfNetwork,
            parent_health_system_id: isPartOfNetwork ? (parentNetworkId || null) : null,
            parent_health_system_name: isPartOfNetwork
              ? (networks.find((n) => n.id === parentNetworkId)?.name || null)
              : null,
          },
          { headers },
        );
        // Reconcile contacts: existingAccount.contacts have ids, new rows don't.
        const existingIds = new Set((existingAccount.contacts || []).map((c) => c.id));
        const keptIds = new Set();
        for (let i = 0; i < cleanContacts.length; i++) {
          const local = cleanContacts[i];
          const matched = (existingAccount.contacts || [])[i];
          if (matched?.id) {
            keptIds.add(matched.id);
            await axios.put(
              `${API}/api/crm/accounts/${existingAccount.id}/contacts/${matched.id}`,
              local,
              { headers },
            );
          } else {
            await axios.post(
              `${API}/api/crm/accounts/${existingAccount.id}/contacts`,
              local,
              { headers },
            );
          }
        }
        // Delete contacts that the user removed from the form
        for (const id of existingIds) {
          if (!keptIds.has(id)) {
            await axios.delete(
              `${API}/api/crm/accounts/${existingAccount.id}/contacts/${id}`,
              { headers },
            );
          }
        }
        toast.success(`${form.name} updated`);
      } else {
        await axios.post(
          `${API}/api/crm/accounts`,
          {
            ...form,
            contacts: cleanContacts,
            force,
            is_part_of_network: isPartOfNetwork,
            parent_health_system_id: isPartOfNetwork ? (parentNetworkId || null) : null,
            parent_health_system_name: isPartOfNetwork
              ? (networks.find((n) => n.id === parentNetworkId)?.name || null)
              : null,
          },
          { headers },
        );
        toast.success(`${form.name} added`);
      }
      onSaved();
    } catch (e) {
      if (e.response?.status === 409) {
        const d = e.response.data?.detail;
        const ex = (d && typeof d === "object") ? d.existing : null;
        setDupe(ex ? { exists: true, ...ex } : { exists: true, name: form.name });
      } else {
        toast.error(e.response?.data?.detail || "Save failed");
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="crm-account-modal">
      <div className="bg-white rounded-lg w-full max-w-2xl my-10 shadow-2xl">
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <h3 className="font-bold text-lg">{isEdit ? `Edit ${existingAccount.name}` : `New ${defaultStatus === "customer" ? "Customer" : "Lead"}`}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        {dupe && dupe.exists && (
          <div className="m-4 bg-amber-50 border-2 border-amber-400 rounded-lg p-4" data-testid="duplicate-popup">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-bold text-amber-900">
                  {dupe.is_legacy ? "Legacy customer already exists" : "This account already exists"}
                </h4>
                <p className="text-sm text-amber-800 mt-1">
                  <strong>{dupe.name}</strong> is already in the CRM
                  {dupe.owner ? <> · assigned to <strong>{dupe.owner.name}</strong></> : <> · <em>unassigned</em></>}
                  {dupe.status && <> · current status: <strong>{dupe.status}</strong></>}
                </p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={onClose} data-testid="dupe-cancel">Cancel</Button>
                  <Button size="sm" onClick={() => submit(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="dupe-force">
                    Add anyway (force duplicate)
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <div className="col-span-2"><Field label="Account Name *">
            <input value={form.name} onChange={(e) => set("name", e.target.value)} onBlur={checkDup} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="acc-name-input" />
          </Field></div>
          <Field label="Type *">
            <div className="flex gap-3">
              {[
                { v: "hospital", l: "Healthcare / Hospital" },
                { v: "non_healthcare", l: "Non-Healthcare" },
              ].map((opt) => (
                <label key={opt.v} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="radio" name="acc-type" value={opt.v} checked={form.type === opt.v} onChange={() => set("type", opt.v)} data-testid={`acc-type-${opt.v}`} />
                  {opt.l}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
              <option value="lead">Lead</option>
              <option value="customer">Customer</option>
            </select>
          </Field>
          <Field label="Pipeline Stage">
            <select value={form.pipeline_stage} onChange={(e) => set("pipeline_stage", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
              {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </Field>
          {isAdmin && (
            <div className="col-span-2">
              <Field label="Assign to Rep">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={form.owner_id}
                      onChange={(e) => set("owner_id", e.target.value)}
                      className="flex-1 min-w-[180px] border border-slate-300 rounded-md px-3 py-2 text-sm"
                      data-testid="acc-owner-select"
                    >
                      <option value="">Unassigned</option>
                      {(reps || []).map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}{r.role_label && r.role_label !== "Sales Rep" ? ` · ${r.role_label}` : ""}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={assignToMe}
                      disabled={creatingRep}
                      className="text-xs font-semibold px-3 py-2 rounded-md bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors disabled:opacity-50"
                      data-testid="acc-assign-to-me"
                    >
                      Assign to me ({(currentUser?.name || "admin").split(" ")[0]})
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddRep(true)}
                      className="text-xs font-semibold px-3 py-2 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors inline-flex items-center gap-1"
                      data-testid="acc-add-rep-toggle"
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      Add Rep
                    </button>
                  </div>
                </div>
              </Field>
            </div>
          )}
          <Field label="Email">
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} onBlur={checkDup} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="acc-email-input" />
          </Field>
          <Field label="Phone">
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>
          <Field label="Website">
            <input value={form.website} onChange={(e) => set("website", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>

          {/* === Healthcare Network Affiliation =========================== */}
          {form.type === "hospital" && (
            <div className="col-span-2 border border-violet-200 bg-violet-50/40 rounded-lg p-4" data-testid="network-affiliation-section">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPartOfNetwork}
                  onChange={(e) => {
                    setIsPartOfNetwork(e.target.checked);
                    if (!e.target.checked) {
                      setParentNetworkId("");
                      setShowNewNetworkForm(false);
                    }
                  }}
                  className="mt-1"
                  data-testid="is-part-of-network-toggle"
                />
                <span>
                  <span className="text-sm font-semibold text-violet-900">
                    This account is part of a larger healthcare network
                  </span>
                  <span className="block text-xs text-violet-700/80 mt-0.5">
                    Link this clinic/hospital under a parent network (e.g., Harris Health System).
                    Aggregates open backorders, YTD spend, and contacts at the network level.
                  </span>
                </span>
              </label>

              {isPartOfNetwork && (
                <div className="mt-3 pl-7 space-y-2">
                  {!showNewNetworkForm && (
                    <>
                      <label className="text-xs uppercase tracking-wider font-bold text-slate-500">
                        Parent network
                      </label>
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          value={parentNetworkId}
                          onChange={(e) => setParentNetworkId(e.target.value)}
                          disabled={networksLoading}
                          className="flex-1 min-w-[200px] border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                          data-testid="parent-network-select"
                        >
                          <option value="">
                            {networksLoading ? "Loading networks…" : "— Select a network —"}
                          </option>
                          {networks.map((n) => (
                            <option key={n.id} value={n.id}>{n.name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setShowNewNetworkForm(true)}
                          className="text-xs font-semibold px-3 py-2 rounded-md bg-violet-600 hover:bg-violet-700 text-white inline-flex items-center gap-1"
                          data-testid="add-new-network-btn"
                        >
                          <Plus className="w-3.5 h-3.5" /> Add new network
                        </button>
                      </div>
                      {parentNetworkId && (
                        <p className="text-xs text-emerald-700 mt-1">
                          ✓ This account will roll up under <strong>{networks.find((n) => n.id === parentNetworkId)?.name}</strong>.
                        </p>
                      )}
                      {!parentNetworkId && networks.length === 0 && !networksLoading && (
                        <p className="text-xs text-amber-700 mt-1">
                          No networks exist yet — click <strong>Add new network</strong> to create the first one.
                        </p>
                      )}
                    </>
                  )}

                  {showNewNetworkForm && (
                    <div className="border-2 border-violet-300 bg-white rounded-lg p-3 space-y-2" data-testid="new-network-form">
                      <p className="text-xs font-bold text-violet-900 uppercase tracking-wider">
                        Create a new healthcare network
                      </p>
                      <input
                        type="text"
                        placeholder="Network name (e.g., Harris Health System)"
                        value={newNetworkName}
                        onChange={(e) => setNewNetworkName(e.target.value)}
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                        data-testid="new-network-name-input"
                        autoFocus
                      />
                      <input
                        type="text"
                        placeholder="State (optional, e.g., TX)"
                        value={newNetworkState}
                        onChange={(e) => setNewNetworkState(e.target.value)}
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                        data-testid="new-network-state-input"
                      />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          disabled={creatingNetwork || !newNetworkName.trim()}
                          onClick={createNewNetwork}
                          className="bg-violet-600 hover:bg-violet-700"
                          data-testid="create-network-submit"
                        >
                          {creatingNetwork ? "Creating…" : "Create & link"}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => { setShowNewNetworkForm(false); setNewNetworkName(""); setNewNetworkState(""); }}
                          data-testid="create-network-cancel"
                        >
                          Cancel
                        </Button>
                      </div>
                      <p className="text-[11px] text-slate-500 leading-snug">
                        The new network will be created as a <strong>pending</strong> health system
                        (not yet an active customer). You can promote it later from
                        Admin → Hospitals.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* === Multi-contact rows (Name · Title · Email · Phone · Primary) === */}
          <div className="col-span-2">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-wider font-bold text-slate-500">
                Contacts <span className="text-slate-400 font-normal normal-case">(add as many as you need — toggle one as Primary)</span>
              </label>
              <button
                type="button"
                onClick={addContactRow}
                className="text-xs font-semibold inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800"
                data-testid="acc-add-contact-row"
              >
                <Plus className="w-3 h-3" /> Add additional contact
              </button>
            </div>
            <div className="space-y-2">
              {contacts.map((c, i) => (
                <div
                  key={i}
                  className={`grid grid-cols-12 gap-2 p-2 rounded border ${c.is_primary ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200 bg-slate-50/50"}`}
                  data-testid={`acc-contact-row-${i}`}
                >
                  <input
                    placeholder="Name"
                    value={c.name}
                    onChange={(e) => setContactRow(i, "name", e.target.value)}
                    className="col-span-3 border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
                    data-testid={`acc-contact-name-${i}`}
                  />
                  <input
                    placeholder="Title (e.g. Contract Specialist)"
                    value={c.title}
                    onChange={(e) => setContactRow(i, "title", e.target.value)}
                    className="col-span-3 border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
                    data-testid={`acc-contact-title-${i}`}
                  />
                  <input
                    type="email"
                    placeholder="email@..."
                    value={c.email}
                    onChange={(e) => setContactRow(i, "email", e.target.value)}
                    className="col-span-3 border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
                    data-testid={`acc-contact-email-${i}`}
                  />
                  <input
                    placeholder="Phone"
                    value={c.phone}
                    onChange={(e) => setContactRow(i, "phone", e.target.value)}
                    className="col-span-2 border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
                  />
                  <div className="col-span-1 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => togglePrimary(i)}
                      title={c.is_primary ? "Primary contact" : "Make primary"}
                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded transition-colors ${c.is_primary ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600 hover:bg-emerald-100"}`}
                      data-testid={`acc-contact-primary-${i}`}
                    >
                      {c.is_primary ? "★" : "☆"}
                    </button>
                    {contacts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeContactRow(i)}
                        className="text-slate-400 hover:text-red-600"
                        title="Remove this contact"
                        data-testid={`acc-contact-remove-${i}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-2"><Field label="Billing Address">
            <textarea rows={2} value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field></div>
          <div className="col-span-2"><Field label="Shipping Address (if different)">
            <textarea rows={2} value={form.shipping_address} onChange={(e) => set("shipping_address", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field></div>
          <div className="col-span-2"><Field label="Notes">
            <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field></div>
        </div>
        <div className="border-t px-6 py-3 flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={() => submit(false)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="acc-save-btn">
            {saving ? "Saving..." : (isEdit ? "Save changes" : "Save")}
          </Button>
        </div>
      </div>

      {/* Shared "Add Rep" dialog — same component used in Hospitals & Settings → Users */}
      <AddSalesRepDialog
        open={showAddRep}
        onOpenChange={setShowAddRep}
        token={(headers?.Authorization || "").replace(/^Bearer\s+/, "")}
        currentUserRole={currentUser?.role}
        onCreated={(rep) => {
          if (setReps) setReps((prev) => [...(prev || []), rep].sort((a, b) => a.name.localeCompare(b.name)));
          set("owner_id", rep.id);
        }}
      />
    </div>
  );
};

// -----------------------------------------------------------------------------
// Account detail modal
// -----------------------------------------------------------------------------
const AccountDetailModal = ({ headers, isAdmin, reps, accountId, onClose, onChanged, onLogActivity, onEdit, onSendCampaign }) => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", email: "", phone: "", title: "", department: "" });
  // Multi-select for "Email N contacts at once" from the Contacts tab
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  // Hospital signup profile drawer (only shown for type=hospital accounts)
  const [showHospitalProfile, setShowHospitalProfile] = useState(false);
  const toggleSelectContact = (id) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllContacts = () => {
    const all = (data?.contacts || []).filter((c) => c.email).map((c) => c.id);
    setSelectedContactIds(new Set(all));
  };
  const clearContactSelection = () => setSelectedContactIds(new Set());

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/crm/accounts/${accountId}`, { headers });
      setData(r.data);
    } catch (e) { toast.error("Failed to load account"); }
  }, [accountId, headers]);
  useEffect(() => { load(); }, [load]);

  const updateStage = async (stage) => {
    try {
      await axios.patch(`${API}/api/crm/accounts/${accountId}`, { pipeline_stage: stage }, { headers });
      toast.success(`Stage → ${stageMeta(stage).label}`);
      load(); onChanged();
    } catch (e) { toast.error("Update failed"); }
  };

  const addContact = async () => {
    if (!newContact.email && !newContact.name) { toast.error("Name or email required"); return; }
    try {
      await axios.post(`${API}/api/crm/accounts/${accountId}/contacts`, newContact, { headers });
      toast.success("Contact added");
      setNewContact({ name: "", email: "", phone: "", title: "", department: "" });
      setShowAddContact(false);
      load();
    } catch (e) { toast.error("Add failed"); }
  };

  const removeContact = async (cid) => {
    if (!window.confirm("Remove this contact?")) return;
    try {
      await axios.delete(`${API}/api/crm/accounts/${accountId}/contacts/${cid}`, { headers });
      load();
    } catch (e) { toast.error("Remove failed"); }
  };

  const setPrimaryContact = async (c) => {
    try {
      await axios.put(
        `${API}/api/crm/accounts/${accountId}/contacts/${c.id}`,
        { ...c, is_primary: true },
        { headers },
      );
      toast.success(`${c.name || c.email} is now the primary contact`);
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const deleteAccount = async () => {
    if (!window.confirm(`Delete ${data?.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/crm/accounts/${accountId}`, { headers });
      toast.success(`${data?.name} deleted`);
      onChanged();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (!data) return null;
  const stage = stageMeta(data.pipeline_stage);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="account-detail-modal">
      <div className="bg-white rounded-lg w-full max-w-4xl my-10 shadow-2xl">
        <div className="border-b px-6 py-4 flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-xl text-slate-900">{data.name}</h3>
              {data.is_legacy && <span className="text-[10px] uppercase font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded">Legacy</span>}
              <span className={`text-xs px-2 py-0.5 rounded border ${stage.color}`}>{stage.label}</span>
              <span className={`text-xs px-2 py-0.5 rounded ${data.status === "customer" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {data.status === "customer" ? "Customer" : "Lead"}
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {data.type === "hospital" ? "🏥 Hospital" : "🏢 Non-Healthcare"} · Owner: <strong>{data.owner?.name || "Unassigned"}</strong>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && data.type === "hospital" && (
              <button
                onClick={() => setShowHospitalProfile(true)}
                className="px-2.5 py-1.5 text-xs font-bold rounded-md bg-cyan-100 text-cyan-800 hover:bg-cyan-200 inline-flex items-center gap-1.5"
                title="View signup profile · Promote to Health System"
                data-testid="detail-hospital-profile"
              >
                <Building2 className="w-3.5 h-3.5" /> Hospital Profile
              </button>
            )}
            {isAdmin && onEdit && (
              <button
                onClick={() => onEdit(data)}
                className="p-1.5 text-slate-500 hover:text-blue-700 rounded hover:bg-blue-50"
                title="Edit account & contacts"
                data-testid="detail-edit-account"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
            {isAdmin && (
              <button
                onClick={deleteAccount}
                className="p-1.5 text-slate-500 hover:text-red-700 rounded hover:bg-red-50"
                title="Delete this account"
                data-testid="detail-delete-account"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-slate-800 rounded hover:bg-slate-100">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="border-b px-6 flex gap-1">
          {["overview", "contacts", "activities", "orders", "history"].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-bold border-b-2 capitalize ${tab === t ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500"}`} data-testid={`detail-tab-${t}`}>
              {t === "activities" ? `Activities (${data.activities?.length || 0})` : t === "orders" ? `Orders (${data.orders?.length || 0})` : t === "contacts" ? `Contacts (${data.contacts?.length || 0})` : t === "history" ? "Approval History" : t}
            </button>
          ))}
        </div>

        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
          {tab === "overview" && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Info l="Email"   v={data.email || "—"} />
              <Info l="Phone"   v={data.phone || "—"} />
              <Info l="Website" v={data.website || "—"} />
              <Info l="Health Score" v={<HealthChip score={data.health_score || 10} />} />
              <Info l="Lifetime Value" v={fmt$(data.lifetime_value)} />
              <Info l="Unpaid Balance" v={fmt$(data.unpaid_balance)} />
              <div className="col-span-2"><Info l="Billing Address" v={<pre className="whitespace-pre-wrap font-sans">{data.billing_address || "—"}</pre>} /></div>
              <div className="col-span-2"><Info l="Shipping Address" v={<pre className="whitespace-pre-wrap font-sans">{data.shipping_address || "—"}</pre>} /></div>
              <div className="col-span-2"><Info l="Notes" v={data.notes || "—"} /></div>
              <div className="col-span-2 flex gap-2">
                <select value={data.pipeline_stage} onChange={(e) => updateStage(e.target.value)} className="border rounded px-3 py-1.5 text-sm" data-testid="detail-stage-select">
                  {STAGES.map((s) => <option key={s.id} value={s.id}>Move to: {s.label}</option>)}
                </select>
                <Button onClick={onLogActivity} className="bg-blue-600 hover:bg-blue-700 gap-1" size="sm" data-testid="detail-log-activity">
                  <Plus className="w-3 h-3" /> Log Activity
                </Button>
              </div>
            </div>
          )}

          {tab === "contacts" && (
            <div className="space-y-2">
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setShowAddContact(!showAddContact)} variant="outline" className="gap-1" data-testid="detail-add-contact">
                  <UserPlus className="w-3 h-3" /> {showAddContact ? "Cancel" : "Add Contact"}
                </Button>
              </div>
              {showAddContact && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 grid grid-cols-2 gap-2">
                  <input placeholder="Name" value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} className="border rounded px-2 py-1.5 text-sm col-span-2" />
                  <input placeholder="Email *" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} className="border rounded px-2 py-1.5 text-sm" />
                  <input placeholder="Phone" value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} className="border rounded px-2 py-1.5 text-sm" />
                  <input placeholder="Title" value={newContact.title} onChange={(e) => setNewContact({ ...newContact, title: e.target.value })} className="border rounded px-2 py-1.5 text-sm" />
                  <input placeholder="Department" value={newContact.department} onChange={(e) => setNewContact({ ...newContact, department: e.target.value })} className="border rounded px-2 py-1.5 text-sm" />
                  <div className="col-span-2 flex justify-end">
                    <Button size="sm" onClick={addContact} className="bg-blue-600 hover:bg-blue-700">Save Contact</Button>
                  </div>
                </div>
              )}
              {(data.contacts || []).length === 0 ? (
                <p className="text-sm text-slate-400 italic text-center py-4">No contacts yet.</p>
              ) : (
                <>
                  {/* Bulk-action bar appears once anything is selected */}
                  {selectedContactIds.size > 0 && (
                    <div className="flex items-center justify-between gap-2 rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs flex-wrap" data-testid="detail-bulk-action-bar">
                      <span className="text-blue-900 font-semibold">
                        {selectedContactIds.size} selected
                      </span>
                      <div className="flex gap-2 items-center flex-wrap">
                        <button onClick={clearContactSelection} className="text-slate-500 hover:text-slate-800" data-testid="detail-bulk-clear">Clear</button>
                        <button onClick={selectAllContacts} className="text-blue-600 hover:underline" data-testid="detail-bulk-select-all">Select all</button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const allSelected = (data.contacts || []).filter((c) => selectedContactIds.has(c.id));
                            const withEmail = allSelected.filter((c) => (c.email || "").trim());
                            const skipped = allSelected.length - withEmail.length;
                            if (withEmail.length === 0) {
                              toast.error("Selected contacts have no email on file");
                              return;
                            }
                            if (skipped > 0) {
                              toast.info(`Skipped ${skipped} contact${skipped === 1 ? "" : "s"} without an email on file`);
                            }
                            // BCC keeps each recipient blind to the others — safer for
                            // hospital comms where you don't want to leak contact lists.
                            const bcc = withEmail.map((c) => encodeURIComponent(c.email.trim())).join(",");
                            const subj = encodeURIComponent(`${data.name} — `);
                            window.location.href = `mailto:?bcc=${bcc}&subject=${subj}`;
                          }}
                          className="gap-1 border-blue-600 text-blue-700 hover:bg-blue-100"
                          data-testid="detail-open-outlook-bcc"
                          title="Open an Outlook draft with all selected contacts in the BCC line"
                        >
                          <Mail className="w-3 h-3" /> Open Outlook draft (BCC)
                        </Button>
                        {onSendCampaign && (
                          <Button
                            size="sm"
                            onClick={() => {
                              const picked = (data.contacts || []).filter((c) => selectedContactIds.has(c.id));
                              onSendCampaign({ ...data, __preselected: picked });
                            }}
                            className="bg-blue-600 hover:bg-blue-700 gap-1"
                            data-testid="detail-email-selected"
                          >
                            <Send className="w-3 h-3" /> Send Campaign ({selectedContactIds.size})
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="divide-y border rounded">
                    {data.contacts.map((c) => {
                      const checked = selectedContactIds.has(c.id);
                      return (
                        <div key={c.id} className={`p-3 flex justify-between items-start ${checked ? "bg-blue-50/40" : ""}`}>
                          <div className="flex items-start gap-2 flex-1">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSelectContact(c.id)}
                              disabled={!c.email}
                              title={c.email ? "Select to bulk-email" : "No email on this contact"}
                              className="mt-1 accent-blue-600 disabled:opacity-30"
                              data-testid={`detail-contact-checkbox-${c.id}`}
                            />
                            <div>
                              <div className="font-semibold flex items-center gap-1.5">
                                {c.name || c.email}
                                {c.is_primary && <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 rounded font-bold">PRIMARY</span>}
                              </div>
                              <div className="text-xs text-slate-500">{c.title} {c.department ? `· ${c.department}` : ""}</div>
                              <div className="text-xs text-blue-600">{c.email}</div>
                              <div className="text-xs text-slate-500">{c.phone}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {onSendCampaign && c.email && (
                              <button
                                onClick={() => onSendCampaign({ ...data, __preselected: [c] })}
                                className="text-slate-500 hover:text-blue-700 px-2 py-1 rounded hover:bg-blue-50 inline-flex items-center gap-1 text-xs"
                                title={`Email ${c.name || c.email}`}
                                data-testid={`detail-email-contact-${c.id}`}
                              >
                                <Mail className="w-3.5 h-3.5" /> Email
                              </button>
                            )}
                            <button
                              onClick={() => setPrimaryContact(c)}
                              disabled={c.is_primary}
                              className={`text-xs px-2 py-1 rounded ${c.is_primary ? "bg-emerald-600 text-white cursor-default" : "text-slate-500 hover:text-emerald-700 hover:bg-emerald-50"}`}
                              title={c.is_primary ? "This is the primary contact" : "Make this the primary contact"}
                              data-testid={`detail-make-primary-${c.id}`}
                            >
                              {c.is_primary ? "★ Primary" : "☆ Make primary"}
                            </button>
                            <button onClick={() => removeContact(c.id)} className="text-slate-400 hover:text-red-600" title="Remove">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "activities" && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={onLogActivity} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="detail-add-activity">
                  <Plus className="w-3 h-3" /> Log Activity
                </Button>
                {onSendCampaign && (
                  <Button
                    size="sm"
                    onClick={() => onSendCampaign(data)}
                    className="bg-blue-600 hover:bg-blue-700 gap-1"
                    data-testid="detail-send-campaign"
                    title="Send a campaign template to this account's contacts without leaving the CRM"
                  >
                    <Send className="w-3 h-3" /> Send Email Campaign
                  </Button>
                )}
              </div>
              {(data.activities || []).length === 0 ? (
                <p className="text-sm text-slate-400 italic text-center py-4">No activities yet.</p>
              ) : (
                <div className="divide-y border rounded">
                  {data.activities.map((a) => {
                    const meta = ACT_META[a.type] || ACT_META.other;
                    const Icon = meta.icon;
                    const openOnCalendar = () => {
                      if (!a.scheduled_at) { toast.error("No date/time"); return; }
                      const start = new Date(a.scheduled_at);
                      const end = new Date(start.getTime() + ((a.duration_minutes || 30) * 60 * 1000));
                      const backLink = `${window.location.origin}/admin?tab=crm&account=${a.account_id || data.id}`;
                      const trail = `\n\nCRM account: ${a.account_name || data.name || ""}\nOpen in dashboard: ${backLink}`;
                      const params = new URLSearchParams({
                        path: "/calendar/action/compose",
                        rru: "addevent",
                        subject: a.subject || `${meta.label} — ${a.account_name || data.name || ""}`,
                        body: (a.body || "") + trail,
                        location: a.location || "",
                        startdt: start.toISOString(),
                        enddt: end.toISOString(),
                      });
                      window.open(`https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`, "_blank");
                    };
                    return (
                      <div key={a.id} className="p-3 text-sm flex gap-3">
                        <Icon className={`w-4 h-4 mt-0.5 ${meta.color}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between gap-2">
                            <span className="font-semibold">
                              {meta.label}{a.subject ? ` — ${a.subject}` : ""}
                              {a.parent_activity_id && (
                                <span className="ml-2 text-[10px] uppercase font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">↳ follow-up</span>
                              )}
                              {a.has_follow_up && (
                                <span className="ml-2 text-[10px] uppercase font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">trail →</span>
                              )}
                            </span>
                            <span className="text-xs text-slate-400 whitespace-nowrap">{fmtDateTime(a.created_at)}</span>
                          </div>
                          {a.body && <p className="text-xs text-slate-600 whitespace-pre-wrap line-clamp-3 mt-1">{a.body}</p>}
                          <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                            {a.assigned_to_name && <>by {a.assigned_to_name} · </>}
                            {a.scheduled_at && (
                              <>
                                <Calendar className="w-3 h-3 text-purple-500" />
                                <span className="text-purple-700 font-semibold">{fmtDateTime(a.scheduled_at)}</span>
                                <button
                                  onClick={openOnCalendar}
                                  className="ml-1 underline decoration-dotted text-purple-600 hover:text-purple-800"
                                  data-testid={`detail-view-calendar-${a.id}`}
                                  title="Re-open in Outlook to confirm the booking"
                                >
                                  View on calendar
                                </button>
                                {" · "}
                              </>
                            )}
                            status: <span className={a.status === "done" ? "text-emerald-600 font-bold" : "text-amber-600"}>{a.status}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "orders" && (
            (data.orders || []).length === 0 ? (
              <p className="text-sm text-slate-400 italic text-center py-4">No supplier orders matched to this account yet.</p>
            ) : (
              <div className="divide-y border rounded">
                {data.orders.map((o) => (
                  <div key={o.id} className="p-3 text-sm flex justify-between items-center">
                    <div>
                      <div className="font-mono font-semibold">{o.po_number || "(no PO)"}</div>
                      <div className="text-xs text-slate-500 truncate max-w-md">{o.description}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{fmt$(o.ext_cost)}</div>
                      <div className="text-xs text-slate-500">{o.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {tab === "history" && <ConversionHistoryPanel accountId={accountId} headers={headers} />}
        </div>

        <div className="border-t px-6 py-3 flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
      <HospitalProfileDialog
        open={showHospitalProfile}
        onClose={() => setShowHospitalProfile(false)}
        accountId={accountId}
        headers={headers}
        onPromoted={() => { onChanged?.(); }}
      />
    </div>
  );
};

const Info = ({ l, v }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide font-bold text-slate-500">{l}</div>
    <div className="text-sm text-slate-800">{v}</div>
  </div>
);

// -----------------------------------------------------------------------------
// Approval History sub-tab — chronological timeline of every approval event
// -----------------------------------------------------------------------------
const ConversionHistoryPanel = ({ accountId, headers }) => {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/crm/accounts/${accountId}/conversion-history`, { headers });
        if (cancelled) return;
        setEvents(r.data?.events || []);
        setStatus(r.data?.current_status || null);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load history");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accountId, headers]);

  if (loading) return <p className="text-sm text-slate-400 italic text-center py-4" data-testid="history-loading">Loading approval history…</p>;
  if (events.length === 0) {
    return (
      <p className="text-sm text-slate-400 italic text-center py-4" data-testid="history-empty">
        No approval requests yet. Reps submit a lead for Customer-Approval from the Leads list.
      </p>
    );
  }

  const dot = (kind) => {
    if (kind === "approved") return "bg-emerald-500";
    if (kind === "rejected") return "bg-rose-500";
    return "bg-amber-500";
  };
  const label = (kind) => {
    if (kind === "approved") return "Approved";
    if (kind === "rejected") return "Rejected";
    return "Approval requested";
  };

  return (
    <div className="space-y-1" data-testid="history-panel">
      <div className="text-xs text-slate-500 mb-3">
        Current status: <strong className="capitalize">{status || "none"}</strong>
      </div>
      <ol className="relative border-l-2 border-slate-200 ml-2 pl-5 space-y-4">
        {events.map((e, idx) => (
          <li key={(e.activity_id || "x") + "-" + e.kind + "-" + idx} className="relative" data-testid={`history-event-${e.kind}-${idx}`}>
            <span className={`absolute -left-7 top-1.5 w-3 h-3 rounded-full ring-4 ring-white ${dot(e.kind)}`} />
            <div className="text-xs uppercase tracking-wide font-bold text-slate-500">{label(e.kind)}</div>
            <div className="text-sm font-semibold text-slate-800">
              {e.actor_name || "—"}
              <span className="text-xs font-normal text-slate-400 ml-2">{fmtDateTime(e.at)}</span>
            </div>
            {e.body && <p className="text-xs text-slate-600 whitespace-pre-wrap mt-1">{e.body}</p>}
            {(e.estimated_first_po_value != null || e.expected_close_date) && (
              <div className="mt-1 text-xs text-slate-500 flex gap-4">
                {e.estimated_first_po_value != null && <span>Est. first PO: <strong>{fmt$(e.estimated_first_po_value)}</strong></span>}
                {e.expected_close_date && <span>Expected close: <strong>{e.expected_close_date}</strong></span>}
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Rep "Request Customer-Approval" modal — replaces the old window.prompt flow
// -----------------------------------------------------------------------------
const RequestConversionModal = ({ account, onClose, onSubmit }) => {
  const [body, setBody] = useState("Ready to convert — first PO confirmed.");
  const [estValue, setEstValue] = useState("");
  const [closeDate, setCloseDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    await onSubmit({
      body: body.trim(),
      estimated_first_po_value: estValue ? Number(estValue) : null,
      expected_close_date: closeDate || null,
    });
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="request-conversion-modal">
      <div className="bg-white rounded-lg w-full max-w-lg shadow-2xl">
        <div className="border-b px-6 py-4 flex justify-between items-start">
          <div>
            <h3 className="font-bold text-lg text-slate-900">Request Customer-Approval</h3>
            <div className="text-xs text-slate-500 mt-0.5">{account.name}</div>
          </div>
          <button onClick={onClose} disabled={submitting} aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-wide font-bold text-slate-600">Justification</label>
            <textarea
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              placeholder="Why is this lead ready to become a customer?"
              data-testid="request-conversion-body"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide font-bold text-slate-600">Estimated first PO ($)</label>
              <input
                type="number"
                min="0"
                step="100"
                value={estValue}
                onChange={(e) => setEstValue(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                placeholder="e.g. 25000"
                data-testid="request-conversion-est-value"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide font-bold text-slate-600">Expected close</label>
              <input
                type="date"
                value={closeDate}
                onChange={(e) => setCloseDate(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                data-testid="request-conversion-close-date"
              />
            </div>
          </div>
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
            On submit we'll notify the assigned admin via Telegram and pop open Outlook with a pre-filled email so you can add any extras.
          </div>
        </div>
        <div className="border-t px-6 py-3 flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !body.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="request-conversion-submit">
            {submitting ? "Submitting…" : "Submit for Approval"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Activity Modal — Email / Phone / Note / Appointment with Outlook integrations
// -----------------------------------------------------------------------------
const ActivityModal = ({ headers, account, reps, isAdmin, onClose, onSaved, parentActivity = null }) => {
  const primary = (account.contacts || []).find((c) => c.is_primary) || (account.contacts || [])[0];
  // When this modal is opened as a follow-up, prefill the contact from the parent
  const initialContactId =
    (parentActivity && (parentActivity.contact_id || "")) ||
    primary?.id ||
    "";
  const [type, setType] = useState(parentActivity ? "phone" : "email");
  // Multi-select contact picker: emails fan out into Outlook BCC; the activity
  // record itself stores the first selected contact (as the canonical link) and
  // surfaces "Name + N others" when more than one is chosen.
  const [contactIds, setContactIds] = useState(() => new Set(initialContactId ? [initialContactId] : []));
  const toggleContactId = (id) => {
    setContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const [subject, setSubject] = useState(
    parentActivity ? `Follow-up: ${parentActivity.subject || parentActivity.type}` : ""
  );
  const [body, setBody] = useState("");
  const [direction, setDirection] = useState("outgoing");
  const [scheduledAt, setScheduledAt] = useState("");
  const [duration, setDuration] = useState(30);
  const [location, setLocation] = useState("");
  const [assignedTo, setAssignedTo] = useState(parentActivity?.assigned_to || "");
  const [saving, setSaving] = useState(false);
  // After-save: surface "Create follow-up?" inline so admin can chain trail
  const [justSaved, setJustSaved] = useState(null);  // the saved activity (when set, show follow-up form)
  const [followUp, setFollowUp] = useState({
    type: "phone",
    subject: "",
    body: "",
    scheduled_at: "",
    duration_minutes: 30,
    location: "",
  });
  const [savingFollowUp, setSavingFollowUp] = useState(false);

  const selectedContacts = (account.contacts || []).filter((c) => contactIds.has(c.id));
  // Back-compat shim: previously a single `selectedContact` was used in calendar
  // links + activity logging. We treat the first selected as the canonical one.
  const selectedContact = selectedContacts[0] || null;
  // Deep-link back into the CRM dashboard for whoever opens the calendar invite
  const backLink = `${window.location.origin}/admin?tab=crm&account=${account.id}`;

  const buildOutlookMailto = () => {
    const emails = selectedContacts.map((c) => (c.email || "").trim()).filter(Boolean);
    const fallback = (account.email || "").trim();
    if (emails.length === 0 && !fallback) {
      toast.error("Pick at least one contact with an email first");
      return;
    }
    // Single recipient → TO line · Multiple → BCC (per user preference: keep
    // recipients blind to each other when fanning out hospital comms).
    let url;
    if (emails.length <= 1) {
      const to = emails[0] || fallback;
      url = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    } else {
      const bcc = emails.map(encodeURIComponent).join(",");
      url = `mailto:?bcc=${bcc}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
    window.location.href = url;
  };

  const buildOutlookCalendarLink = () => {
    if (!scheduledAt) { toast.error("Pick a date/time first so it can land on your calendar"); return null; }
    const start = new Date(scheduledAt);
    const end = new Date(start.getTime() + duration * 60 * 1000);
    // Outlook web compose deep link with a trailing link back to the CRM dashboard
    const trail =
      (parentActivity ? `\n\n↳ Follow-up to: ${parentActivity.subject || parentActivity.type}` : "") +
      `\n\nCRM account: ${account.name}\nOpen in dashboard: ${backLink}`;
    const params = new URLSearchParams({
      path: "/calendar/action/compose",
      rru: "addevent",
      subject: subject || `${(type[0]?.toUpperCase() + type.slice(1)) || "Activity"} — ${account.name}`,
      body: (body || "") + trail,
      location: location || "",
      startdt: start.toISOString(),
      enddt: end.toISOString(),
      to: selectedContact?.email || account.email || "",
    });
    return `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`;
  };

  const submit = async ({ openOutlook = false, openCalendar = false } = {}) => {
    setSaving(true);
    try {
      const firstContact = selectedContacts[0] || null;
      const contactNameLabel = !firstContact
        ? null
        : selectedContacts.length === 1
          ? (firstContact.name || firstContact.email)
          : `${firstContact.name || firstContact.email} + ${selectedContacts.length - 1} other${selectedContacts.length - 1 === 1 ? "" : "s"}`;
      const res = await axios.post(`${API}/api/crm/activities`, {
        account_id: account.id,
        contact_id: firstContact?.id || null,
        contact_name: contactNameLabel,
        type, direction,
        subject, body,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        duration_minutes: duration,
        location,
        assigned_to: assignedTo || null,
        status: "not_started",
        parent_activity_id: parentActivity?.id || null,
      }, { headers });
      const created = res.data;

      // Phase 5 — Multi-recipient fan-out. When this is an email touch with
      // 2+ contacts, mirror the activity onto every secondary contact's
      // timeline as a child of the just-created parent. Each contact then
      // has an accurate "last touched" date without us needing to denormalize
      // anything in the UI.
      const fannedOut = [];
      if (type === "email" && selectedContacts.length > 1 && created?.id) {
        const others = selectedContacts.slice(1);
        const childPayloads = others.map((c) => ({
          account_id: account.id,
          contact_id: c.id,
          contact_name: c.name || c.email,
          type, direction,
          subject, body,
          scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
          duration_minutes: duration,
          location,
          assigned_to: assignedTo || null,
          status: "not_started",
          parent_activity_id: created.id,
        }));
        // Best-effort fan-out — don't block the user on individual failures.
        const settled = await Promise.allSettled(
          childPayloads.map((p) => axios.post(`${API}/api/crm/activities`, p, { headers }))
        );
        settled.forEach((s, i) => {
          if (s.status === "fulfilled") fannedOut.push(others[i]);
          else console.warn("Child activity fan-out failed", others[i].id, s.reason);
        });
      }

      if (fannedOut.length > 0) {
        toast.success(`Activity logged · mirrored to ${fannedOut.length} other contact${fannedOut.length === 1 ? "" : "s"}`);
      } else {
        toast.success(parentActivity ? "Follow-up logged" : "Activity logged");
      }

      if (openOutlook && type === "email") buildOutlookMailto();
      if (openCalendar) {
        const link = buildOutlookCalendarLink();
        if (link) window.open(link, "_blank");
        if (scheduledAt) {
          // Also offer .ics download for non-Outlook calendars
          window.open(`${API}/api/crm/activities/${created.id}/calendar.ics`, "_blank");
        }
      }

      // Show the inline follow-up prompt instead of closing immediately.
      // Caller decides via 'Save Only' to close, or this branch surfaces the prompt.
      setJustSaved(created);
      setFollowUp({
        type: "phone",
        subject: "",
        body: "",
        scheduled_at: "",
        duration_minutes: 30,
        location: "",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveFollowUp = async ({ openCalendar = false } = {}) => {
    if (!justSaved?.id) return;
    setSavingFollowUp(true);
    try {
      const res = await axios.post(
        `${API}/api/crm/activities/${justSaved.id}/follow-up`,
        {
          type: followUp.type,
          subject: followUp.subject || null,
          body: followUp.body || null,
          status: "not_started",
          scheduled_at: followUp.scheduled_at ? new Date(followUp.scheduled_at).toISOString() : null,
          duration_minutes: followUp.duration_minutes,
          location: followUp.location || null,
        },
        { headers }
      );
      const created = res.data;
      toast.success("Follow-up trail extended");
      if (openCalendar && followUp.scheduled_at) {
        const start = new Date(followUp.scheduled_at);
        const end = new Date(start.getTime() + (followUp.duration_minutes || 30) * 60 * 1000);
        const trail =
          `\n\n↳ Follow-up to: ${justSaved.subject || justSaved.type}` +
          `\nCRM account: ${account.name}\nOpen in dashboard: ${backLink}`;
        const params = new URLSearchParams({
          path: "/calendar/action/compose",
          rru: "addevent",
          subject: followUp.subject || `Follow-up: ${justSaved.subject || justSaved.type} — ${account.name}`,
          body: (followUp.body || "") + trail,
          location: followUp.location || "",
          startdt: start.toISOString(),
          enddt: end.toISOString(),
          to: selectedContact?.email || account.email || "",
        });
        window.open(`https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`, "_blank");
        window.open(`${API}/api/crm/activities/${created.id}/calendar.ics`, "_blank");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Follow-up failed");
    } finally {
      setSavingFollowUp(false);
    }
  };

  const closeAfterSave = () => {
    if (justSaved) onSaved();
    else onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="activity-modal">
      <div className="bg-white rounded-lg w-full max-w-2xl my-10 shadow-2xl">
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <h3 className="font-bold text-lg">
            {parentActivity ? "Log follow-up" : "Log Activity"} — {account.name}
          </h3>
          <button onClick={closeAfterSave}><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {parentActivity && (
            <div className="rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2 text-xs text-blue-900">
              <span className="font-semibold">Trailing:</span>{" "}
              {parentActivity.subject || `${parentActivity.type} on ${fmtDateTime(parentActivity.created_at)}`}
            </div>
          )}
          <div className="flex gap-2">
            {Object.entries(ACT_META).filter(([k]) => k !== "other").map(([k, m]) => {
              const Icon = m.icon;
              return (
                <button key={k} onClick={() => setType(k)} className={`flex-1 px-3 py-2 rounded border-2 text-sm font-bold inline-flex items-center justify-center gap-1.5 ${type === k ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-400"}`} data-testid={`act-type-${k}`}>
                  <Icon className="w-4 h-4" /> {m.label}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={`Contact${contactIds.size > 1 ? "s" : ""} ${contactIds.size > 0 ? `· ${contactIds.size} selected` : ""}`}>
              <div className="border rounded text-sm bg-white" data-testid="act-contact-multiselect">
                {(account.contacts || []).length === 0 ? (
                  <p className="px-3 py-2 text-xs text-slate-400 italic">No contacts on this account.</p>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-2 px-2 py-1 border-b text-[11px] text-slate-500">
                      <button
                        type="button"
                        onClick={() => {
                          const all = (account.contacts || []).filter((c) => c.email).map((c) => c.id);
                          setContactIds(new Set(all));
                        }}
                        className="text-blue-600 hover:underline"
                        data-testid="act-contact-select-all"
                      >Select all with email</button>
                      <button
                        type="button"
                        onClick={() => setContactIds(new Set())}
                        className="text-slate-500 hover:text-slate-800"
                        data-testid="act-contact-clear"
                      >Clear</button>
                    </div>
                    <div className="max-h-36 overflow-y-auto">
                      {(account.contacts || []).map((c) => {
                        const checked = contactIds.has(c.id);
                        return (
                          <label
                            key={c.id}
                            className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-slate-50 ${checked ? "bg-blue-50/60" : ""}`}
                            title={c.email ? "" : "No email on this contact"}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleContactId(c.id)}
                              disabled={!c.email}
                              className="accent-blue-600 disabled:opacity-30"
                              data-testid={`act-contact-checkbox-${c.id}`}
                            />
                            <span className="font-semibold text-slate-900 truncate">{c.name || c.email}</span>
                            {c.email && <span className="text-blue-600 text-xs truncate">{c.email}</span>}
                            {c.is_primary && <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 rounded font-bold ml-auto shrink-0">PRIMARY</span>}
                          </label>
                        );
                      })}
                    </div>
                    {contactIds.size > 1 && type === "email" && (
                      <p className="px-2 py-1 text-[10px] text-blue-700 bg-blue-50 border-t">
                        Multiple recipients → Outlook will open with all addresses in <strong>BCC</strong> (kept blind to each other).
                      </p>
                    )}
                  </>
                )}
              </div>
            </Field>
            <Field label="Direction">
              <select value={direction} onChange={(e) => setDirection(e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
                <option value="outgoing">Outgoing</option>
                <option value="incoming">Incoming</option>
              </select>
            </Field>
          </div>

          {(type === "email" || type === "appointment" || type === "phone" || type === "note") && (
            <Field label="Subject">
              <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" placeholder={type === "appointment" ? "Demo call with Liberty Hospital" : type === "email" ? "Re: Implant Workflow & Rebate" : "Subject"} data-testid="act-subject" />
            </Field>
          )}

          <div className="grid grid-cols-3 gap-3">
            <Field label={`Date / Time${type === "appointment" ? " *" : ""}`}>
              <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" data-testid="act-scheduled" />
            </Field>
            <Field label="Duration (min)">
              <input type="number" value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="w-full border rounded px-3 py-2 text-sm" />
            </Field>
            <Field label="Location">
              <input value={location} onChange={(e) => setLocation(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" placeholder="Phone / Zoom / On-site" />
            </Field>
          </div>

          <Field label={type === "email" ? "Body — built here, opens in Outlook" : "Notes"}>
            <textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} className="w-full border rounded px-3 py-2 text-sm font-mono" data-testid="act-body" />
          </Field>

          {isAdmin && (
            <Field label="Assign To">
              <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
                <option value="">— me —</option>
                {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </Field>
          )}

          {/* === Inline follow-up trail (appears after the activity is saved) === */}
          {justSaved && (
            <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50/70 p-3 space-y-2" data-testid="follow-up-section">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-900">
                  Saved. Create a follow-up?
                </span>
                <span className="text-[11px] text-emerald-700 ml-auto">
                  Links back to {justSaved.subject || justSaved.type} so the trail stays connected.
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Follow-up type">
                  <select
                    value={followUp.type}
                    onChange={(e) => setFollowUp({ ...followUp, type: e.target.value })}
                    className="w-full border rounded px-3 py-2 text-sm"
                    data-testid="follow-up-type"
                  >
                    {Object.entries(ACT_META).filter(([k]) => k !== "other").map(([k, m]) => (
                      <option key={k} value={k}>{m.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="When (optional — needed for calendar)">
                  <input
                    type="datetime-local"
                    value={followUp.scheduled_at}
                    onChange={(e) => setFollowUp({ ...followUp, scheduled_at: e.target.value })}
                    className="w-full border rounded px-3 py-2 text-sm"
                    data-testid="follow-up-scheduled"
                  />
                </Field>
              </div>
              <Field label="Subject">
                <input
                  value={followUp.subject}
                  onChange={(e) => setFollowUp({ ...followUp, subject: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder={`Follow-up: ${justSaved.subject || justSaved.type}`}
                  data-testid="follow-up-subject"
                />
              </Field>
              <Field label="Notes">
                <textarea
                  rows={2}
                  value={followUp.body}
                  onChange={(e) => setFollowUp({ ...followUp, body: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="e.g. Asked to circle back next Tuesday with the rebate schedule."
                />
              </Field>
              <div className="flex gap-2 justify-end pt-1">
                <Button
                  variant="outline"
                  onClick={() => { setJustSaved(null); onSaved(); }}
                  disabled={savingFollowUp}
                  data-testid="follow-up-skip"
                >
                  Skip
                </Button>
                <Button
                  onClick={() => saveFollowUp({ openCalendar: false })}
                  disabled={savingFollowUp}
                  className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
                  data-testid="follow-up-save"
                >
                  {savingFollowUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
                  Save follow-up
                </Button>
                <Button
                  onClick={() => saveFollowUp({ openCalendar: true })}
                  disabled={savingFollowUp || !followUp.scheduled_at}
                  className="bg-blue-600 hover:bg-blue-700 gap-1.5"
                  data-testid="follow-up-save-calendar"
                  title={!followUp.scheduled_at ? "Pick a date/time first" : "Save + add to Outlook calendar"}
                >
                  <Calendar className="w-4 h-4" />
                  Save + Outlook calendar
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="border-t px-6 py-3 flex flex-wrap justify-end gap-2 bg-slate-50">
          {justSaved ? (
            <Button variant="outline" onClick={() => { setJustSaved(null); onSaved(); }} data-testid="act-close">Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
              <Button onClick={() => submit()} disabled={saving} variant="outline" data-testid="act-save-only">
                {saving ? "Saving..." : "Save Only"}
              </Button>
              {type === "email" && (
                <Button onClick={() => submit({ openOutlook: true })} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="act-save-outlook">
                  <ExternalLink className="w-4 h-4" />
                  Save & Open in Outlook
                </Button>
              )}
              <Button
                onClick={() => submit({ openCalendar: true })}
                disabled={saving || !scheduledAt}
                className="bg-purple-600 hover:bg-purple-700 gap-2 text-white"
                data-testid="act-save-calendar"
                title={!scheduledAt ? "Pick a date/time to enable calendar save" : "Save activity + open Outlook calendar"}
              >
                <Calendar className="w-4 h-4" />
                Save to Outlook calendar
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Activities list (top-level tab)
// -----------------------------------------------------------------------------
const ActivitiesList = ({ activities, headers, isAdmin, accounts, onChanged, onOpenAccount, onOpenActivity, onSendCampaign }) => {
  const [statusFilter, setStatusFilter] = useState("open");
  const [typeFilter, setTypeFilter] = useState("all");
  const filtered = activities.filter((a) => {
    if (statusFilter === "open" && a.status === "done") return false;
    if (statusFilter === "done" && a.status !== "done") return false;
    if (typeFilter !== "all" && a.type !== typeFilter) return false;
    return true;
  });

  // Re-open the same Outlook calendar event in a new tab. We can't fetch a
  // protected ICS endpoint from window.open (no bearer header), so we rebuild
  // the Outlook web compose deep-link client-side from the activity itself.
  // The dedupe rule on Outlook (matching subject + start) means the existing
  // event will surface, letting the user verify it's still on the calendar.
  const openOutlookFor = (a) => {
    if (!a.scheduled_at) {
      toast.error("No date/time on this activity yet");
      return;
    }
    try {
      const start = new Date(a.scheduled_at);
      const end = new Date(start.getTime() + ((a.duration_minutes || 30) * 60 * 1000));
      const backLink = `${window.location.origin}/admin?tab=crm&account=${a.account_id}`;
      const trail = `\n\nCRM account: ${a.account_name || ""}\nOpen in dashboard: ${backLink}`;
      const params = new URLSearchParams({
        path: "/calendar/action/compose",
        rru: "addevent",
        subject: a.subject || `${(a.type[0]?.toUpperCase() + a.type.slice(1))} — ${a.account_name || ""}`,
        body: (a.body || "") + trail,
        location: a.location || "",
        startdt: start.toISOString(),
        enddt: end.toISOString(),
      });
      window.open(`https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`, "_blank");
    } catch {
      toast.error("Couldn't build a calendar link from this activity");
    }
  };

  const complete = async (id) => {
    try {
      await axios.post(`${API}/api/crm/activities/${id}/complete`, {}, { headers });
      toast.success("Marked done");
      onChanged();
    } catch (e) { toast.error("Failed"); }
  };
  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/api/crm/activities/${id}`, { status }, { headers });
      toast.success(status === "in_progress" ? "Marked in progress" : "Status updated");
      onChanged();
    } catch (e) { toast.error("Failed to update status"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this activity?")) return;
    await axios.delete(`${API}/api/crm/activities/${id}`, { headers });
    onChanged();
  };

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border rounded px-3 py-2 text-sm" data-testid="act-status-filter">
          <option value="open">Open Activities</option>
          <option value="done">Completed</option>
          <option value="all">All</option>
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="border rounded px-3 py-2 text-sm" data-testid="act-type-filter">
          <option value="all">All types</option>
          {Object.entries(ACT_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <span className="text-xs text-slate-500 ml-auto">{filtered.length} activities</span>
        {onSendCampaign && (
          <Button
            size="sm"
            onClick={() => onSendCampaign()}
            className="bg-blue-600 hover:bg-blue-700 gap-1.5 text-white"
            data-testid="activities-send-campaign"
            title="Open the email-campaign quick-send dialog from the activity feed"
          >
            <Send className="w-3.5 h-3.5" /> Send Email Campaign
          </Button>
        )}
      </div>
      <div className="bg-white border rounded-lg divide-y" data-testid="activities-list">
        {filtered.length === 0 && <p className="text-sm text-slate-400 italic text-center py-12">No activities match.</p>}
        {filtered.map((a) => {
          const meta = ACT_META[a.type] || ACT_META.other;
          const Icon = meta.icon;
          const notesCount = (a.notes_log || []).length;
          return (
            <div key={a.id} className="p-3 flex gap-3 hover:bg-slate-50" data-testid={`activity-row-${a.id}`}>
              <button
                type="button"
                onClick={() => onOpenActivity && onOpenActivity(a)}
                className="flex gap-3 flex-1 min-w-0 text-left"
                data-testid={`activity-open-${a.id}`}
                title="Open / Edit activity"
              >
                <Icon className={`w-4 h-4 mt-1 ${meta.color}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-2">
                    <span className="font-semibold text-slate-800 text-left">
                      <span className="hover:text-blue-600">{a.account_name}</span>
                      <span className="text-slate-500 font-normal"> · {meta.label}</span>
                      {a.subject && <span className="text-slate-700"> — {a.subject}</span>}
                      {a.parent_activity_id && (
                        <span className="ml-2 text-[10px] uppercase font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" title="Follow-up activity">
                          ↳ follow-up
                        </span>
                      )}
                      {a.has_follow_up && (
                        <span className="ml-2 text-[10px] uppercase font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded" title="Has a follow-up activity">
                          trail →
                        </span>
                      )}
                      {notesCount > 0 && (
                        <span className="ml-2 text-[10px] uppercase font-bold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5" title={`${notesCount} note${notesCount === 1 ? "" : "s"}`}>
                          <MessageSquare className="w-2.5 h-2.5" /> {notesCount}
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-slate-400 whitespace-nowrap">{fmtDateTime(a.created_at)}</span>
                  </div>
                  {a.body && <p className="text-xs text-slate-600 line-clamp-2 mt-1 whitespace-pre-wrap">{a.body}</p>}
                  <div className="text-xs text-slate-400 mt-1">
                    {a.contact_name && <>↳ {a.contact_name} · </>}
                    {a.assigned_to_name && <>{a.assigned_to_name} · </>}
                    {a.scheduled_at && (
                      <>
                        <Calendar className="w-3 h-3 inline-block -mt-0.5 mr-0.5 text-purple-500" />
                        <span className="text-purple-700 font-semibold">{fmtDateTime(a.scheduled_at)}</span>
                        {" · "}
                      </>
                    )}
                    status: <span className={a.status === "done" ? "text-emerald-600 font-bold" : a.status === "in_progress" ? "text-blue-600 font-bold" : "text-amber-600 font-bold"}>{(a.status || "not_started").replace("_", " ")}</span>
                  </div>
                </div>
              </button>
              <div className="flex gap-1">
                {a.account_id && (
                  <button onClick={() => onOpenAccount(a.account_id)} className="p-1 text-slate-400 hover:text-blue-600" title="Open account" data-testid={`activity-open-account-${a.id}`}>
                    <Building2 className="w-4 h-4" />
                  </button>
                )}
                {a.scheduled_at && (
                  <button
                    onClick={() => openOutlookFor(a)}
                    className="p-1 text-slate-400 hover:text-purple-600"
                    title="View on Outlook calendar"
                    data-testid={`view-calendar-${a.id}`}
                  >
                    <Calendar className="w-4 h-4" />
                  </button>
                )}
                {a.status !== "done" && a.status !== "in_progress" && (
                  <button
                    onClick={() => setStatus(a.id, "in_progress")}
                    className="p-1 text-slate-400 hover:text-blue-600"
                    title="Mark in progress"
                    data-testid={`activity-progress-${a.id}`}
                  >
                    <PlayCircle className="w-4 h-4" />
                  </button>
                )}
                {a.status !== "done" && (
                  <button onClick={() => complete(a.id)} className="p-1 text-slate-400 hover:text-emerald-600" title="Mark done" data-testid={`activity-done-${a.id}`}>
                    <CheckCircle className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => onOpenActivity && onOpenActivity(a)} className="p-1 text-slate-400 hover:text-indigo-600" title="Edit / Add notes" data-testid={`activity-edit-${a.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => del(a.id)} className="p-1 text-slate-400 hover:text-red-600" title="Delete" data-testid={`activity-delete-${a.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

// -----------------------------------------------------------------------------
// ActivityDetailModal — open, edit, change status, and add notes timeline
// -----------------------------------------------------------------------------
const ActivityDetailModal = ({ headers, activity, reps, accounts, onClose, onSaved, onDeleted }) => {
  const [a, setA] = useState(activity);
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  // ISO -> "YYYY-MM-DDTHH:mm" for datetime-local input
  const toLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const setField = (k, v) => setA((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        type: a.type,
        subject: a.subject,
        body: a.body,
        status: a.status,
        scheduled_at: a.scheduled_at ? new Date(a.scheduled_at).toISOString() : null,
        due_date: a.due_date || null,
        duration_minutes: a.duration_minutes ? Number(a.duration_minutes) : null,
        location: a.location,
        assigned_to: a.assigned_to || null,
        related_to: a.related_to,
        direction: a.direction,
      };
      await axios.patch(`${API}/api/crm/activities/${a.id}`, payload, { headers });
      toast.success("Activity updated");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update activity");
    } finally { setSaving(false); }
  };

  const quickSetStatus = async (status) => {
    try {
      const { data } = await axios.patch(`${API}/api/crm/activities/${a.id}`, { status }, { headers });
      setA(data);
      toast.success(`Status: ${status.replace("_", " ")}`);
    } catch (e) { toast.error("Failed to update status"); }
  };

  const markDone = async () => {
    try {
      const { data } = await axios.post(`${API}/api/crm/activities/${a.id}/complete`, {}, { headers });
      setA(data);
      toast.success("Marked done");
    } catch (e) { toast.error("Failed to mark done"); }
  };

  const addNote = async () => {
    const t = newNote.trim();
    if (!t) return;
    setAddingNote(true);
    try {
      const { data } = await axios.post(`${API}/api/crm/activities/${a.id}/notes`, { text: t }, { headers });
      setA(data);
      setNewNote("");
      toast.success("Note added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add note");
    } finally { setAddingNote(false); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      const { data } = await axios.delete(`${API}/api/crm/activities/${a.id}/notes/${noteId}`, { headers });
      setA(data);
    } catch (e) { toast.error("Failed to delete note"); }
  };

  const del = async () => {
    if (!window.confirm("Delete this activity entirely?")) return;
    try {
      await axios.delete(`${API}/api/crm/activities/${a.id}`, { headers });
      toast.success("Activity deleted");
      onDeleted && onDeleted();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const meta = ACT_META[a.type] || ACT_META.other;
  const Icon = meta.icon;
  const notes = a.notes_log || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="activity-detail-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={`w-5 h-5 ${meta.color}`} />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{a.account_name || "Activity"}</p>
              <p className="text-xs text-slate-500 truncate">{meta.label}{a.subject ? ` · ${a.subject}` : ""}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={del} className="p-1.5 rounded text-slate-400 hover:bg-red-50 hover:text-red-600" title="Delete activity" data-testid="activity-detail-delete">
              <Trash2 className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid="activity-detail-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Status quick-actions */}
        <div className="px-5 pt-4 flex flex-wrap gap-2 items-center">
          <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status:</span>
          <button
            onClick={() => quickSetStatus("not_started")}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${a.status === "not_started" ? "bg-amber-100 border-amber-300 text-amber-800" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}
            data-testid="activity-status-not-started"
          >
            <PauseCircle className="w-3.5 h-3.5 inline-block -mt-0.5 mr-1" />
            Not Started
          </button>
          <button
            onClick={() => quickSetStatus("in_progress")}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${a.status === "in_progress" ? "bg-blue-100 border-blue-300 text-blue-800" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}
            data-testid="activity-status-in-progress"
          >
            <PlayCircle className="w-3.5 h-3.5 inline-block -mt-0.5 mr-1" />
            In Progress
          </button>
          <button
            onClick={markDone}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${a.status === "done" ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}
            data-testid="activity-status-done"
          >
            <CheckCircle className="w-3.5 h-3.5 inline-block -mt-0.5 mr-1" />
            Done
          </button>
          {a.completed_at && (
            <span className="ml-auto text-xs text-emerald-600 inline-flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Completed {fmtDateTime(a.completed_at)}
            </span>
          )}
        </div>

        {/* Editable fields */}
        <div className="p-5 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Type</label>
            <select
              value={a.type || "note"}
              onChange={(e) => setField("type", e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm"
              data-testid="activity-detail-type"
            >
              {Object.entries(ACT_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Assigned To</label>
            <select
              value={a.assigned_to || ""}
              onChange={(e) => setField("assigned_to", e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm"
              data-testid="activity-detail-assigned"
            >
              <option value="">— Unassigned —</option>
              {(reps || []).map((r) => <option key={r.id} value={r.id}>{r.name || r.email}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-slate-600 block mb-1">Subject</label>
            <Input
              value={a.subject || ""}
              onChange={(e) => setField("subject", e.target.value)}
              placeholder="Activity subject..."
              data-testid="activity-detail-subject"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-slate-600 block mb-1">Details / Body</label>
            <textarea
              value={a.body || ""}
              onChange={(e) => setField("body", e.target.value)}
              rows={4}
              className="w-full border rounded px-3 py-2 text-sm font-mono"
              placeholder="Full description of the activity..."
              data-testid="activity-detail-body"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Scheduled Date/Time</label>
            <Input
              type="datetime-local"
              value={toLocal(a.scheduled_at)}
              onChange={(e) => setField("scheduled_at", e.target.value)}
              data-testid="activity-detail-scheduled"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Duration (minutes)</label>
            <Input
              type="number"
              min="0"
              value={a.duration_minutes || ""}
              onChange={(e) => setField("duration_minutes", e.target.value)}
              data-testid="activity-detail-duration"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Due Date</label>
            <Input
              type="date"
              value={a.due_date ? String(a.due_date).slice(0, 10) : ""}
              onChange={(e) => setField("due_date", e.target.value)}
              data-testid="activity-detail-due-date"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Location</label>
            <Input
              value={a.location || ""}
              onChange={(e) => setField("location", e.target.value)}
              placeholder="On-site, video call, phone…"
              data-testid="activity-detail-location"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-slate-600 block mb-1">Related To (PO #, Quote #, ref)</label>
            <Input
              value={a.related_to || ""}
              onChange={(e) => setField("related_to", e.target.value)}
              data-testid="activity-detail-related-to"
            />
          </div>
        </div>

        {/* Notes timeline */}
        <div className="px-5 pb-5">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-bold text-slate-800">Notes ({notes.length})</h3>
          </div>
          <div className="flex gap-2 mb-3">
            <Input
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addNote(); } }}
              placeholder="Add a note and press Enter…"
              data-testid="activity-detail-new-note"
            />
            <Button
              onClick={addNote}
              disabled={!newNote.trim() || addingNote}
              className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1"
              data-testid="activity-detail-add-note"
            >
              {addingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add Note
            </Button>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="activity-detail-notes-list">
            {notes.length === 0 && (
              <p className="text-xs text-slate-400 italic text-center py-4">No notes yet. Add the first one above.</p>
            )}
            {[...notes].reverse().map((n) => (
              <div key={n.id} className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex gap-3 group" data-testid={`activity-note-${n.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{n.text}</p>
                  <p className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {fmtDateTime(n.created_at)} · {n.author_name || "Unknown"}
                  </p>
                </div>
                <button
                  onClick={() => deleteNote(n.id)}
                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition-opacity"
                  title="Delete note"
                  data-testid={`activity-note-delete-${n.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex items-center justify-between gap-2">
          <span className="text-xs text-slate-500">Created {fmtDateTime(a.created_at)}{a.updated_at ? ` · Updated ${fmtDateTime(a.updated_at)}` : ""}</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="activity-detail-cancel">Cancel</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
              data-testid="activity-detail-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};



// -----------------------------------------------------------------------------
// Send Email composer (top-level tab)
// -----------------------------------------------------------------------------
const SendEmailComposer = ({ accounts, headers, onLogged }) => {
  const [accountId, setAccountId] = useState("");
  const [contactId, setContactId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const acc = accounts.find((a) => a.id === accountId);
  const contact = (acc?.contacts || []).find((c) => c.id === contactId);
  const toEmail = contact?.email || acc?.email || "";

  const sendViaOutlook = async () => {
    if (!toEmail) { toast.error("Pick an account/contact with an email"); return; }
    setSaving(true);
    try {
      // Log it as an Email Outgoing activity
      await axios.post(`${API}/api/crm/activities`, {
        account_id: accountId,
        contact_id: contactId || null,
        contact_name: contact?.name || contact?.email || null,
        type: "email", direction: "outgoing",
        subject, body, status: "not_started",
      }, { headers });
      // open Outlook
      const url = `mailto:${encodeURIComponent(toEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = url;
      toast.success("Logged & opened in Outlook");
      onLogged();
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border rounded-lg p-5 space-y-4" data-testid="send-email-composer">
      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
        Compose your message here — clicking <strong>Open in Outlook</strong> auto-logs it as an Email Outgoing activity AND opens your Outlook compose window pre-filled.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Account *">
          <select value={accountId} onChange={(e) => { setAccountId(e.target.value); setContactId(""); }} className="w-full border rounded px-3 py-2 text-sm" data-testid="send-account">
            <option value="">— pick account —</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>
        <Field label="Contact">
          <select value={contactId} onChange={(e) => setContactId(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" data-testid="send-contact" disabled={!acc}>
            <option value="">— pick contact (or use account email) —</option>
            {(acc?.contacts || []).map((c) => (
              <option key={c.id} value={c.id}>{(c.name || c.email)} {c.email ? `· ${c.email}` : ""}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="To">
        <input value={toEmail} readOnly className="w-full border rounded px-3 py-2 text-sm bg-slate-50" data-testid="send-to" />
      </Field>
      <Field label="Subject">
        <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" data-testid="send-subject" />
      </Field>
      <Field label="Body">
        <textarea rows={12} value={body} onChange={(e) => setBody(e.target.value)} className="w-full border rounded px-3 py-2 text-sm font-mono" data-testid="send-body" />
      </Field>
      <div className="flex justify-end">
        <Button onClick={sendViaOutlook} disabled={saving || !accountId} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="send-open-outlook">
          <Mail className="w-4 h-4" /> Save & Open in Outlook
        </Button>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Reassign-by-Domain modal — pick a rep, multi-select domains, preview, execute
// -----------------------------------------------------------------------------
const DomainReassignModal = ({ headers, reps, domains, onClose, onSaved }) => {
  const [repId, setRepId] = useState(reps[0]?.id || "");
  const [unassign, setUnassign] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = (d) => {
    const next = new Set(picked);
    if (next.has(d)) next.delete(d); else next.add(d);
    setPicked(next);
    setPreview(null);
  };

  const visible = useMemo(() => {
    return domains.filter((d) => !search || d.domain.includes(search.toLowerCase()));
  }, [domains, search]);

  const doPreview = async () => {
    if (picked.size === 0) { toast.error("Pick at least one domain"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/crm/accounts/reassign-by-domain`, {
        rep_id: unassign ? "__unassign__" : repId,
        domains: Array.from(picked),
        only_unassigned: onlyUnassigned,
        preview: true,
      }, { headers });
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const execute = async () => {
    if (picked.size === 0) { toast.error("Pick at least one domain"); return; }
    if (!unassign && !repId) { toast.error("Pick a rep or check Unassign"); return; }
    const repName = unassign ? "Unassigned" : (reps.find((r) => r.id === repId)?.name || "?");
    const ok = window.confirm(`Reassign accounts matching ${Array.from(picked).join(", ")} to ${repName}?`);
    if (!ok) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/crm/accounts/reassign-by-domain`, {
        rep_id: unassign ? "__unassign__" : repId,
        domains: Array.from(picked),
        only_unassigned: onlyUnassigned,
        preview: false,
      }, { headers });
      toast.success(`Reassigned ${r.data.modified} accounts`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="domain-modal">
      <div className="bg-white rounded-lg w-full max-w-3xl my-10 shadow-2xl">
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-blue-600" />
            Reassign Accounts by Email Domain
          </h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
            Match logic: every account whose own email <strong>OR any contact email</strong> ends with the picked domain(s).
            Use this to onboard a new rep in seconds (e.g., assign every <code>@memorialhermann.org</code> to John Smith).
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Assign to Rep">
              <select value={repId} onChange={(e) => setRepId(e.target.value)} disabled={unassign} className="w-full border rounded px-3 py-2 text-sm" data-testid="domain-rep">
                <option value="">— pick rep —</option>
                {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </Field>
            <Field label="Or Unassign">
              <label className="flex items-center gap-2 mt-2 text-sm">
                <input type="checkbox" checked={unassign} onChange={(e) => setUnassign(e.target.checked)} data-testid="domain-unassign" />
                Set to Unassigned
              </label>
            </Field>
            <Field label="Scope">
              <label className="flex items-center gap-2 mt-2 text-sm">
                <input type="checkbox" checked={onlyUnassigned} onChange={(e) => setOnlyUnassigned(e.target.checked)} data-testid="domain-only-unassigned" />
                Only currently-unassigned
              </label>
            </Field>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-xs font-bold uppercase text-slate-600">Pick domain(s) — {picked.size} selected</label>
              <input placeholder="filter…" value={search} onChange={(e) => setSearch(e.target.value)} className="text-xs border rounded px-2 py-1 w-40" data-testid="domain-search" />
            </div>
            <div className="border rounded max-h-72 overflow-y-auto divide-y" data-testid="domain-list">
              {visible.length === 0 && <p className="p-4 text-center text-slate-400 italic text-sm">No domains found.</p>}
              {visible.map((d) => {
                const checked = picked.has(d.domain);
                return (
                  <label key={d.domain} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 ${checked ? "bg-blue-50" : ""}`} data-testid={`domain-row-${d.domain}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(d.domain)} />
                    <div className="flex-1">
                      <div className="font-mono text-sm font-bold">@{d.domain}</div>
                      <div className="text-xs text-slate-500">
                        {d.accounts} account{d.accounts === 1 ? "" : "s"} ·{" "}
                        {d.owners.map((o) => `${o.rep_name}: ${o.count}`).join(" · ")}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {preview && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm" data-testid="domain-preview">
              <strong>Preview:</strong> would reassign <strong className="text-2xl">{preview.total}</strong> account{preview.total === 1 ? "" : "s"}.
              {preview.sample.length > 0 && (
                <ul className="mt-2 text-xs text-slate-700 list-disc list-inside max-h-32 overflow-y-auto">
                  {preview.sample.slice(0, 20).map((m) => <li key={m.id}>{m.name}{m.email ? ` — ${m.email}` : ""}</li>)}
                  {preview.total > 20 && <li className="italic">…and {preview.total - 20} more</li>}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="border-t px-6 py-3 flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={doPreview} disabled={busy || picked.size === 0} variant="outline" data-testid="domain-preview-btn">Preview</Button>
          <Button onClick={execute} disabled={busy || picked.size === 0} className="bg-blue-600 hover:bg-blue-700" data-testid="domain-execute-btn">
            {busy ? "Working..." : "Reassign"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CrmManager;